import { validateDownload } from '@/lib/downloadValidation';
import DesignRequirements from '@/components/DesignRequirements';
/* Existing effects intentionally preserve lifecycle semantics. */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Loader2, Mic, MicOff, Download,
  Sparkles, Box, Code, Package,
  CheckCircle, RotateCw, Image as ImageIcon,
  Cpu, ThermometerSun, Scale3D, Presentation,
  Target, Clock, DollarSign, Users, Lightbulb,
  Factory, TrendingUp, AlertTriangle, ArrowRight,
  X, FileText, ChevronRight, Layers, Play, Pause,
  Activity, Gauge, Shield, Zap, Share2, Twitter, Linkedin, Copy, Check,
  LayoutTemplate, Crown, Volume2, HelpCircle, Info, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { costCurrency, formatCostValue, isCostReconciled } from '@/lib/costPresentation';
import { groupWarnings, warningSummary } from '@/lib/qualityWarnings';
import { ProProjectSkeleton } from '@/components/skeletons/Skeletons';
import AnnotatedDrawingViewer from '@/components/moat/AnnotatedDrawingViewer';
import TutorialModal from './studio/TutorialModal';
import SocialShareModal from './studio/SocialShareModal';
import TemplatesModal from './studio/TemplatesModal';
import SimulationCard from './studio/SimulationCard';
import ExplodedViewAnimation from './studio/ExplodedViewAnimation';
import UpgradeModal from './studio/UpgradeModal';
import IntentReviewModal from './IntentReviewModal';
import CompetitiveEdgeModal from './CompetitiveEdgeModal';
import { ChatBubble, PresentationCard } from './studio/presentational';
import { API_URL } from '@/utils/apiUrl';


const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1500);
};

const usableModelUrl = (value) => {
  if (typeof value !== 'string') return '';
  const candidate = value.trim();
  if (!candidate || /^(?:error\s*:|login required|unauthorized|forbidden)/i.test(candidate)) return '';
  return /^(?:https?:\/\/|\/)/i.test(candidate) ? candidate : '';
};

// Generated images live on the API origin. In production the React app has a
// different origin, so a raw `/api/assets/...` src would otherwise request the
// static frontend and render as a broken image.
const resolveAssetUrl = (value) => {
  if (typeof value !== 'string') return '';
  const candidate = value.trim();
  if (!candidate) return '';
  return candidate.startsWith('/api/') && API_URL ? `${API_URL}${candidate}` : candidate;
};

const usableConceptViews = (views) => Object.fromEntries(
  Object.entries(views || {}).filter(([, value]) => Boolean(resolveAssetUrl(value)))
);

const formatFieldLabel = (key) => String(key)
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const StructuredValue = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-slate-500">—</span>;
  }
  if (Array.isArray(value)) {
    return (
      <ul className="space-y-1 list-disc pl-5">
        {value.map((item, index) => <li key={index}><StructuredValue value={item} /></li>)}
      </ul>
    );
  }
  if (typeof value === 'object') {
    return (
      <dl className="space-y-2">
        {Object.entries(value).map(([key, item]) => (
          <div key={key} className="border-b border-white/5 pb-2 last:border-0 last:pb-0">
            <dt className="text-[10px] font-mono uppercase tracking-wider text-amber-300/80 mb-1">{formatFieldLabel(key)}</dt>
            <dd className="text-xs text-slate-300 leading-relaxed"><StructuredValue value={item} /></dd>
          </div>
        ))}
      </dl>
    );
  }
  if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'string' && /^[\[{]/.test(value.trim())) {
    try {
      return <StructuredValue value={JSON.parse(value)} />;
    } catch {
      // Keep malformed provider text visible as text instead of hiding it.
    }
  }
  return <span>{String(value)}</span>;
};


export default function DesignStudio() {
  const { language, t: tt } = useLanguage();
  const sectionRef = useRef(null);
  const proResultRef = useRef(null);
  // Helper: pull a mesh3d key with EN fallback baked into LanguageContext
  const m3 = (key) => tt(`mesh3d.${key}`);

  // State
  const [chatMessage, setChatMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isChatting, setIsChatting] = useState(false);
  const [design, setDesign] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [sessionId] = useState(() => globalThis.crypto?.randomUUID?.() || `session-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
  const [isListening, setIsListening] = useState(false); // kept for backward compat with hint UI

  // Concept image state (AI generated)
  const [conceptImage, setConceptImage] = useState(null);

  // Simulation state
  const [simulationResult, setSimulationResult] = useState(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [showSimulationModal, setShowSimulationModal] = useState(false);

  // Schematic assembly-sequence animation state
  const [explodedViewData, setExplodedViewData] = useState(null);
  const [isExplodedPlaying, setIsExplodedPlaying] = useState(false);
  const [isGeneratingExploded, setIsGeneratingExploded] = useState(false);
  const [showExplodedModal, setShowExplodedModal] = useState(false);

  // Presentation state
  const [presentation, setPresentation] = useState(null);
  const [isGeneratingPresentation, setIsGeneratingPresentation] = useState(false);
  const [showPresentation, setShowPresentation] = useState(false);

  // 3D Prototype state
  const [prototypeData, setPrototypeData] = useState(null);
  const [isGenerating3D, setIsGenerating3D] = useState(false);
  const [show3DViewer, setShow3DViewer] = useState(false);
  const [model3DLoaded, setModel3DLoaded] = useState(false);
  const [model3DError, setModel3DError] = useState('');
  const [meshyTaskId, setMeshyTaskId] = useState(null);
  const [meshyStatus, setMeshyStatus] = useState(null);
  const [meshyProgress, setMeshyProgress] = useState(0);
  const [model3DUrl, setModel3DUrl] = useState(null);
  const [rawGlbUrl, setRawGlbUrl] = useState(null);

  // Social Share state
  const [showShareModal, setShowShareModal] = useState(false);

  // Onshape CAD state
  const [isCreatingCAD, setIsCreatingCAD] = useState(false);
  const [isExportingCad, setIsExportingCad] = useState(null);
  const [copilotMenuOpen, setCopilotMenuOpen] = useState(false);
  const addChatMessage = (text) => setChatHistory(prev => [...prev, { text, isUser: false, isSystem: true }]);
  const [cadDocument, setCadDocument] = useState(null);
  const [showCADSuccess, setShowCADSuccess] = useState(false);

  // Usage limit state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isDemo, setIsDemo] = useState(true);
  const [designCount, setDesignCount] = useState(0);
  const [userPlan, setUserPlan] = useState('free');
  const [designLimit, setDesignLimit] = useState(3);
  const [quotaReady, setQuotaReady] = useState(false);

  // PRO PROJECT MODE STATE
  const [studioMode, setStudioMode] = useState('standard'); // standard, pro
  const [proProject, setProProject] = useState(null);
  const [isGeneratingProProject, setIsGeneratingProProject] = useState(false);
  const [proProjectType, setProProjectType] = useState('product');
  // A complete Pro package includes starter software/firmware by default.
  // Users can opt out for purely mechanical concepts.
  const [includeCode, setIncludeCode] = useState(true);
  const [showProProjectResult, setShowProProjectResult] = useState(false);
  const [activeTab, setActiveTab] = useState('summary'); // For Pro Project tabs

  useEffect(() => {
    const applyStudioMode = (value) => {
      if (value === 'pro' || value === 'standard') setStudioMode(value);
    };
    const onStudioMode = (event) => applyStudioMode(event.detail?.studioMode);
    window.addEventListener('nexus:set-studio-mode', onStudioMode);
    try {
      const requestedMode = sessionStorage.getItem('nexus-studio-mode');
      if (requestedMode) {
        sessionStorage.removeItem('nexus-studio-mode');
        applyStudioMode(requestedMode);
      }
    } catch { /* storage may be unavailable in locked-down browsers */ }
    return () => window.removeEventListener('nexus:set-studio-mode', onStudioMode);
  }, []);

  // Tutorial state - ONLY shown when user clicks "How to Use" button
  // Auto-show removed for cleaner, investor-ready first impression
  const [showTutorial, setShowTutorial] = useState(false);

  const handleDontShowTutorialAgain = () => {
    localStorage.setItem('nexus_tutorial_dismissed', 'true');
    setShowTutorial(false);
  };

  // Check user status and design count on mount
  useEffect(() => {
    checkUserStatus();
  }, []);

  const checkUserStatus = async () => {
    try {
      // Skip user lookup entirely if there's no session token — avoids
      // bogus 401 noise in the browser console for first-time visitors.
      const storedToken =
        localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      if (!storedToken) {
        setIsDemo(true);
        const quotaRes = await fetch(`${API_URL}/api/design/quota`, { credentials: 'include' });
        if (quotaRes.ok) {
          const quota = await quotaRes.json();
          if (Number.isFinite(quota.used)) setDesignCount(quota.used);
          if (Number.isFinite(quota.limit)) setDesignLimit(quota.limit === -1 ? 9999 : quota.limit);
        } else {
          const demoCount = parseInt(localStorage.getItem('nexus_demo_designs') || '0');
          setDesignCount(demoCount);
          setDesignLimit(3);
        }
        return;
      }

      // Check if logged in
      const userRes = await fetch(`${API_URL}/api/user/me`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${storedToken}` },
      });
      if (userRes.ok) {
        const userData = await userRes.json();
        setIsDemo(false);

        // Cache email for admin resolver (resolveCurrentEmail falls back to this)
        if (userData?.email) {
          try { localStorage.setItem('user_email', userData.email); } catch {}
        }

        // Get subscription
        const subRes = await fetch(`${API_URL}/api/user/subscription`, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        if (subRes.ok) {
          const subData = await subRes.json();
          setUserPlan(subData.plan_id || 'free');
          setDesignLimit(subData.designs_per_month === -1 ? 9999 : subData.designs_per_month || 3);
        }

        // Get design count this month
        const designsRes = await fetch(`${API_URL}/api/user/designs`, {
          credentials: 'include',
          headers: { Authorization: `Bearer ${storedToken}` },
        });
        if (designsRes.ok) {
          const designsData = await designsRes.json();
          // Server is authoritative so clearing browser storage cannot reset quota.
          if (Number.isFinite(designsData.monthly_count)) {
            setDesignCount(designsData.monthly_count);
          }
          if (Number.isFinite(designsData.design_limit)) {
            setDesignLimit(designsData.design_limit === -1 ? 9999 : designsData.design_limit);
          }
        }
      } else {
        // Invalid/expired session: fall back to the server-authoritative anonymous quota.
        setIsDemo(true);
        const quotaRes = await fetch(`${API_URL}/api/design/quota`, { credentials: 'include' });
        if (quotaRes.ok) {
          const quota = await quotaRes.json();
          if (Number.isFinite(quota.used)) setDesignCount(quota.used);
          if (Number.isFinite(quota.limit)) setDesignLimit(quota.limit === -1 ? 9999 : quota.limit);
        }
      }
    } catch (err) {
      if (err?.name !== 'TypeError') console.error('Status check error:', err);
      // Local storage is display-only fallback when the quota endpoint is unreachable.
      setIsDemo(true);
      const demoCount = parseInt(localStorage.getItem('nexus_demo_designs') || '0');
      setDesignCount(demoCount);
      setDesignLimit(3);
    } finally {
      setQuotaReady(true);
    }
  };

  // Admin emails - these users have unlimited access (must match backend ADMIN_EMAILS)
  const ADMIN_EMAILS = [
    'admin@nexusdesignai.com',
    'alihan@nexusdesignai.com',
    'alihan.demirhan@outlook.com',
    'alihandemirhan3@gmail.com',
  ];

  // Multi-source admin detection — survives partial auth failures in production.
  // Checks: (1) user_email key, (2) parsed user JSON in `user` key, (3) window.__nexus_user if set.
  const resolveCurrentEmail = () => {
    try {
      const direct = localStorage.getItem('user_email') || sessionStorage.getItem('user_email');
      if (direct) return direct.toLowerCase();
      const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.email) return String(parsed.email).toLowerCase();
      }
      if (typeof window !== 'undefined' && window.__nexus_user?.email) {
        return String(window.__nexus_user.email).toLowerCase();
      }
    } catch {}
    return '';
  };

  const canCreateDesign = () => {
    const email = resolveCurrentEmail();
    if (email && ADMIN_EMAILS.map((e) => e.toLowerCase()).includes(email)) {
      return true; // Admin has unlimited access
    }

    // Admin and unlimited plan users have unlimited access
    if (userPlan === 'enterprise' || userPlan === 'unlimited' || userPlan === 'admin') return true;

    // The backend is authoritative for both anonymous IP quota and account quota.
    return quotaReady && designCount < designLimit;
  };

  // Check if current user is admin
  const isAdminUser = () => {
    const email = resolveCurrentEmail();
    return !!email && ADMIN_EMAILS.map((e) => e.toLowerCase()).includes(email);
  };

  const markDemoTrialUsed = () => {
    // Don't mark demo as used for admin users
    if (isAdminUser()) return;
    localStorage.setItem('nexus_demo_trial_used', 'true');
  };

  const incrementDesignCount = () => {
    // Don't increment for admin users
    if (isAdminUser()) return;
    setDesignCount((prev) => {
      const newCount = prev + 1;
      if (isDemo) {
        localStorage.setItem('nexus_demo_designs', newCount.toString());
      }
      return newCount;
    });
  };

  const handleLogin = () => {
    window.location.href = `${API_URL}/api/auth/google/login?redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}`;
  };

  const hasSessionToken = () => {
    if (typeof window === 'undefined') return false;
    return Boolean(
      localStorage.getItem('session_token') || sessionStorage.getItem('session_token')
    );
  };

  const requireSignedInTool = (toolName) => {
    if (hasSessionToken()) return true;
    setChatHistory(prev => [...prev, {
      text: language === 'tr'
        ? `🔐 ${toolName} için giriş yapmanız gerekiyor. Tasarımınız kaybolmadı; giriş yaptıktan sonra bu aracı yeniden çalıştırın.`
        : `🔐 Sign in to use ${toolName}. Your design is safe; run this tool again after signing in.`,
      isUser: false,
      isSystem: true,
    }]);
    return false;
  };

  // PRO PROJECT GENERATION
  const [proProjectError, setProProjectError] = useState(null); // {message, code, requiresLogin}
  const [proProjectProgress, setProProjectProgress] = useState(0);
  const [proProjectStage, setProProjectStage] = useState('');

  // Detailed Brief panel state — lets users pin specific requirements
  const [showDetailedBrief, setShowDetailedBrief] = useState(false);
  const [briefMustInclude, setBriefMustInclude] = useState('');   // newline-separated
  const [briefMustExclude, setBriefMustExclude] = useState('');
  const [briefTargetUsers, setBriefTargetUsers] = useState('');
  const [briefOperatingEnv, setBriefOperatingEnv] = useState('');
  const [briefPowerNotes, setBriefPowerNotes] = useState('');     // user-friendly text → power_spec
  const [briefExtraNotes, setBriefExtraNotes] = useState('');
  // Complete prototype view set: exterior orthographic + spatial + internal assembly.
  const [briefViews, setBriefViews] = useState(['front', 'side', 'top', 'iso', 'exploded', 'cutaway']);

  // ──────────────────────────────────────────────────────────────────────
  // INTENT LOCK — pre-generation intent review (prevents prompt drift)
  // ──────────────────────────────────────────────────────────────────────
  const [intentModalOpen, setIntentModalOpen] = useState(false);
  const [intentLoading, setIntentLoading] = useState(false);
  const [parsedIntent, setParsedIntent] = useState(null);
  const [intentFallback, setIntentFallback] = useState(false);
  const [intentError, setIntentError] = useState(null);

  // ──────────────────────────────────────────────────────────────────────
  // COMPETITIVE EDGE — "design something better than X and Y"
  // ──────────────────────────────────────────────────────────────────────
  const [edgeModalOpen, setEdgeModalOpen] = useState(false);
  // Remember the edge-research result so we can pass extra context into generation
  const [lastEdgeData, setLastEdgeData] = useState(null);

  const openEdgeEngine = () => {
    if (!chatMessage.trim() || isGeneratingProProject) return;
    if (!canCreateDesign()) { setShowUpgradeModal(true); return; }
    setEdgeModalOpen(true);
  };

  // Step 1: open the modal and call parse-intent
  const openIntentReview = async () => {
    if (!chatMessage.trim() || isGeneratingProProject) return;

    // The backend intentionally supports a limited anonymous Pro demo. Intent
    // parsing is an authenticated, paid pre-flight step, so demo users skip it
    // and start the capability-token-protected generation job directly.
    if (!hasSessionToken()) {
      await generateProProject(null);
      return;
    }
    if (!canCreateDesign()) {
      setShowUpgradeModal(true);
      return;
    }
    setIntentModalOpen(true);
    setIntentLoading(true);
    setParsedIntent(null);
    setIntentFallback(false);
    setIntentError(null);
    try {
      const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/project/parse-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          prompt: chatMessage,
          language: language,
          project_type: proProjectType,
          must_include: briefMustInclude.trim()
            ? briefMustInclude.split(/\n|,/).map((item) => item.trim()).filter(Boolean)
            : undefined,
          must_exclude: briefMustExclude.trim()
            ? briefMustExclude.split(/\n|,/).map((item) => item.trim()).filter(Boolean)
            : undefined,
          target_users: briefTargetUsers.trim() || undefined,
          operating_environment: briefOperatingEnv.trim() || undefined,
          power_spec: briefPowerNotes.trim()
            ? { description: briefPowerNotes.trim() }
            : undefined,
          extra_notes: briefExtraNotes.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (data && data.intent) {
        setParsedIntent(data.intent);
        setIntentFallback(!!data.fallback);
      } else {
        setIntentError(data?.detail || 'Parse failed');
      }
    } catch (err) {
      setIntentError(err.message || String(err));
    } finally {
      setIntentLoading(false);
    }
  };

  // Last confirmed intent — stays around until next generation so we can show
  // the Fidelity Card on the result panel ("you asked for X — here is X delivered").
  const [lastConfirmedIntent, setLastConfirmedIntent] = useState(null);
  const [lastOriginalPrompt, setLastOriginalPrompt] = useState('');

  const generateProProject = async (confirmedIntent = null, promptOverride = null) => {
    const projectPrompt = String(promptOverride || chatMessage || '').trim();
    if (!projectPrompt || isGeneratingProProject) return;

    if (!canCreateDesign()) {
      setShowUpgradeModal(true);
      return;
    }

    setIsGeneratingProProject(true);
    setProProject(null);
    setProProjectError(null);
    setProProjectProgress(0);
    setProProjectStage('starting');
    // Capture the intent + original prompt so the Fidelity Card can render after success
    if (confirmedIntent) setLastConfirmedIntent(confirmedIntent);
    setLastOriginalPrompt(projectPrompt);

    const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json',
      ...(storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {})
    };
    const isLoggedIn = !!storedToken;
    const isDemoTrial = !isLoggedIn;

    // Build structured constraint payload from the optional Detailed Brief fields
    const splitLines = (s) => s.split(/\n|,/).map(l => l.trim()).filter(Boolean);
    const must_include = briefMustInclude.trim() ? splitLines(briefMustInclude) : null;
    const must_exclude = briefMustExclude.trim() ? splitLines(briefMustExclude) : null;
    const power_spec = briefPowerNotes.trim() ? { description: briefPowerNotes.trim() } : null;

    try {
      // Step 1: Kick off the job (returns immediately with job_id)
      const startRes = await fetch(`${API_URL}/api/project/generate-full`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          prompt: projectPrompt,
          language: language,
          project_type: proProjectType,
          include_code: includeCode,
          include_3d: true,
          is_demo_trial: isDemoTrial,
          views: briefViews && briefViews.length ? briefViews : undefined,
          must_include: must_include || undefined,
          must_exclude: must_exclude || undefined,
          target_users: briefTargetUsers.trim() || undefined,
          operating_environment: briefOperatingEnv.trim() || undefined,
          power_spec: power_spec || undefined,
          extra_notes: briefExtraNotes.trim() || undefined,
          parsed_intent: confirmedIntent || undefined,
          competitive_edge: lastEdgeData || undefined,
        })
      });

      if (!startRes.ok) {
        const errData = await startRes.json().catch(() => ({}));
        const detail = typeof errData.detail === 'string' ? errData.detail : (errData.detail?.message || 'Proje oluşturulamadı');
        const requiresLogin = startRes.status === 401 || startRes.status === 429 || /login|giriş/i.test(detail);
        setProProjectError({ message: detail, code: startRes.status, requiresLogin });
        return;
      }

      const { job_id, capability_token } = await startRes.json();
      if (!job_id) throw new Error('No job_id returned');

      // Anonymous/demo jobs are protected by a scoped capability token.
      // Forward it while polling or the successfully-started job returns 401.
      const pollHeaders = {
        ...headers,
        ...(capability_token ? { 'X-Project-Capability': capability_token } : {}),
      };

      // Step 2: Poll the job status. Resilient — survives short transient errors,
      // backs off slightly between failed polls, and surfaces clear errors only after persistent failure.
      const POLL_INTERVAL_MS = 3000;
      const MAX_POLL_ATTEMPTS = 100;       // up to 5 minutes
      const TRANSIENT_RETRY_TOLERANCE = 6;  // tolerate up to ~18s of transient errors
      let consecutiveErrors = 0;

      for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));

        let job = null;
        try {
          const pollRes = await fetch(`${API_URL}/api/project/job/${job_id}`, {
            method: 'GET',
            headers: pollHeaders,
            credentials: 'include',
          });
          if (!pollRes.ok) {
            consecutiveErrors += 1;
            if (consecutiveErrors > TRANSIENT_RETRY_TOLERANCE) {
              throw new Error(`Job poll failed after ${consecutiveErrors} attempts (HTTP ${pollRes.status})`);
            }
            continue;
          }
          job = await pollRes.json();
          consecutiveErrors = 0;
        } catch (pollErr) {
          consecutiveErrors += 1;
          if (consecutiveErrors > TRANSIENT_RETRY_TOLERANCE) throw pollErr;
          continue;
        }

        setProProjectProgress(job.progress || 0);
        setProProjectStage(job.stage || '');

        // Live image updates — first view typically lands a few seconds after status=done
        if (job.concept_image || (job.concept_views && Object.keys(job.concept_views).length)) {
          setProProject((prev) => prev ? {
            ...prev,
            concept_image: job.concept_image || prev.concept_image,
            concept_views: { ...(prev.concept_views || {}), ...(job.concept_views || {}) },
            artifact_status: job.artifact_status || prev.artifact_status,
          } : prev);
        }

        if (job.status === 'done') {
          // Success — shape the response like the old synchronous API for compatibility
          setProProject({
            success: true,
            project_id: job.project_id,
            project: job.project,
            code: job.code,
            concept_image: job.concept_image || null,
            concept_views: job.concept_views || {},
            artifact_status: job.artifact_status || {},
            validation: job.validation || null,
            requirement_graph: job.requirement_graph || null,
          });
          setShowProProjectResult(true);
          window.setTimeout(() => {
            proResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
          if (isDemoTrial) markDemoTrialUsed();
          incrementDesignCount();
          setCurrentPrompt(projectPrompt);
          setChatMessage('');
          // Continue polling for up to 3 minutes to pick up bounded background image results
          // (image generation runs in background after analysis completes)
          (async () => {
            for (let j = 0; j < 45; j++) {
              await new Promise((r) => setTimeout(r, 4000));
              try {
                const r = await fetch(`${API_URL}/api/project/job/${job_id}`, { headers: pollHeaders, credentials: 'include' });
                if (!r.ok) continue;
                const fresh = await r.json();
                setProProject((prev) => prev ? {
                  ...prev,
                  concept_image: fresh.concept_image || prev.concept_image,
                  concept_views: { ...(prev.concept_views || {}), ...(fresh.concept_views || {}) },
                  artifact_status: fresh.artifact_status || prev.artifact_status,
                } : prev);
                const visualStatus = fresh?.artifact_status?.visuals?.status;
                // A terminal state matters even when zero images were returned;
                // otherwise blank tiles remain stuck on "Generating" forever.
                if (['ready', 'partial', 'blocked'].includes(visualStatus)) break;
              } catch { /* ignore — silent background polling */ }
            }
          })();
          return;
        }

        if (job.status === 'failed') {
          const errMsg = job.error || (language === 'tr' ? 'Proje oluşturulamadı' : 'Project generation failed');
          const requiresLogin = /budget|quota|limit|login|giriş/i.test(errMsg);
          setProProjectError({ message: errMsg, code: 500, requiresLogin });
          return;
        }
      }

      // Timeout
      throw new Error(language === 'tr'
        ? 'Proje oluşturma beklenmedik şekilde uzun sürdü. Lütfen tekrar deneyin.'
        : 'Project generation took unexpectedly long. Please try again.');
    } catch (err) {
      console.error('Pro project error:', err);
      const raw = String(err?.message || err);
      // Map opaque browser-network errors to user-friendly Turkish/English messages
      let friendly = raw;
      if (/Failed to fetch|Load failed|NetworkError|net::|TypeError: fetch/i.test(raw)) {
        friendly = language === 'tr'
          ? 'Bağlantı hatası. Sunucu yeniden başlatılıyor olabilir — lütfen 5-10 saniye sonra tekrar deneyin.'
          : 'Connection error. The server may be restarting — please try again in 5-10 seconds.';
      } else if (/abort|AbortError/i.test(raw)) {
        friendly = language === 'tr'
          ? 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.'
          : 'Request timed out. Please try again.';
      }
      setProProjectError({
        message: friendly,
        code: 0,
        requiresLogin: false,
      });
    } finally {
      setIsGeneratingProProject(false);
      setProProjectProgress(0);
      setProProjectStage('');
    }
  };

  // Download project as JSON
  const downloadProjectJSON = () => {
    if (!proProject) return;
    const dataStr = JSON.stringify(proProject, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    triggerBlobDownload(blob, `project_${proProject.project_id || 'export'}.json`);
  };

  // ============================================================================
  // IMAGE-TO-3D (provider-neutral multi-view pipeline) — generates a downloadable GLB
  // model from the project's 4 concept views.
  // ============================================================================
  const [mesh3dStatus, setMesh3dStatus] = useState('idle'); // idle | starting | running | completed | failed
  const [mesh3dStage, setMesh3dStage] = useState('');
  const [mesh3dProgress, setMesh3dProgress] = useState(0);
  const [mesh3dError, setMesh3dError] = useState(null);
  const [mesh3dModelUrl, setMesh3dModelUrl] = useState(null);
  const [mesh3dFileSize, setMesh3dFileSize] = useState(null);

  const formatBytes = (bytes) => {
    if (!bytes || !Number.isFinite(Number(bytes))) return '';
    const b = Number(bytes);
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Reset mesh state whenever a new Pro Project is generated/cleared
  useEffect(() => {
    setMesh3dStatus('idle');
    setMesh3dStage('');
    setMesh3dProgress(0);
    setMesh3dError(null);
    setMesh3dModelUrl(null);
    setMesh3dFileSize(null);
    // If the project doc already has a generated model attached, surface it
    const existing = usableModelUrl(proProject?.mesh_3d?.model_url);
    if (existing) {
      setMesh3dStatus('completed');
      setMesh3dProgress(100);
      setMesh3dModelUrl(existing);
      setMesh3dFileSize(proProject?.mesh_3d?.file_size || null);
    }
  }, [proProject?.project_id]);

  const generate3DMesh = async () => {
    const projectId = proProject?.project_id;
    if (!projectId) return;
    if (mesh3dStatus === 'starting' || mesh3dStatus === 'running') return;

    const views = proProject?.concept_views || {};
    const viewCount = Object.keys(views).length || (proProject?.concept_image ? 1 : 0);
    if (viewCount === 0) {
      setMesh3dError(m3('noViews'));
      setMesh3dStatus('failed');
      return;
    }

    setMesh3dStatus('starting');
    setMesh3dStage('queued');
    setMesh3dProgress(2);
    setMesh3dError(null);
    setMesh3dModelUrl(null);

    try {
      const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const meshHeaders = storedToken ? { Authorization: `Bearer ${storedToken}` } : {};
      const res = await fetch(`${API_URL}/api/project/${projectId}/generate-3d`, {
        method: 'POST',
        credentials: 'include',
        headers: meshHeaders,
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || `HTTP ${res.status}`);
      }

      // Already-generated short-circuit
      if (data.already_generated && data.model_url) {
        const existingModelUrl = usableModelUrl(data.model_url);
        if (!existingModelUrl) throw new Error('3D generation returned an invalid model file');
        setMesh3dStatus('completed');
        setMesh3dProgress(100);
        setMesh3dModelUrl(existingModelUrl);
        setMesh3dFileSize(data.file_size || null);
        return;
      }

      const jobId = data.job_id;
      if (!jobId) throw new Error('No job_id returned');

      setMesh3dStatus('running');
      setMesh3dStage('uploading_images');
      setMesh3dProgress(10);

      // Poll up to ~3 min (60 × 3s). Network failures are retryable;
      // a terminal backend failure must surface immediately with its real message.
      const maxAttempts = 60;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 3000));
        let pr;
        try {
          pr = await fetch(`${API_URL}/api/project/3d-job/${jobId}`, {
            credentials: 'include',
            headers: meshHeaders,
          });
        } catch (pollErr) {
          console.warn('[mesh3d poll network]', pollErr);
          continue;
        }
        if (!pr.ok) continue;
        const job = await pr.json();
        if (typeof job.progress === 'number') setMesh3dProgress(job.progress);
        if (job.stage) setMesh3dStage(job.stage);

        if (job.status === 'completed') {
          const completedModelUrl = usableModelUrl(job.model_url);
          if (!completedModelUrl) throw new Error('3D generation completed without a valid model file');
          setMesh3dStatus('completed');
          setMesh3dProgress(100);
          setMesh3dModelUrl(completedModelUrl);
          setMesh3dFileSize(job.file_size || null);
          return;
        }
        if (job.status === 'failed') {
          throw new Error(job.error || '3D generation failed');
        }
      }
      throw new Error(m3('timeout'));
    } catch (e) {
      setMesh3dStatus('failed');
      const raw = String(e?.message || e);
      // Map provider failures to stable, user-safe messages
      let friendly = raw;
      if (/balance|Exhausted|billing/i.test(raw)) {
        friendly = language === 'tr'
          ? '3D üretim hizmeti geçici olarak kullanılamıyor. Daha sonra tekrar deneyin veya destekle iletişime geçin.'
          : 'The 3D generation service is temporarily unavailable. Try again later or contact support.';
      } else if (/not configured/i.test(raw)) {
        friendly = language === 'tr'
          ? 'Bu dağıtımda 3D üretim kullanılamıyor. Destekle iletişime geçin.'
          : '3D generation is unavailable for this deployment. Contact support.';
      } else if (/login required|unauth(?:orized)?|authentication required|session expired/i.test(raw)) {
        friendly = language === 'tr'
          ? 'Oturumunuz sona ermiş olabilir. Yeniden giriş yapıp 3B üretimi tekrar deneyin.'
          : 'Your session may have expired. Sign in again and retry 3D generation.';
      }
      setMesh3dError(friendly);
    }
  };

  // Pro means a complete prototype pipeline: once visual evidence lands,
  // start the rotatable GLB automatically instead of hiding it behind another click.
  useEffect(() => {
    if (!proProject?.project_id || mesh3dStatus !== 'idle') return undefined;
    const viewCount = Object.keys(proProject?.concept_views || {}).length || (proProject?.concept_image ? 1 : 0);
    if (viewCount < 1) return undefined;
    const timer = window.setTimeout(() => generate3DMesh(), 700);
    return () => window.clearTimeout(timer);
  }, [proProject?.project_id, proProject?.concept_image, proProject?.concept_views, mesh3dStatus]);



  // Templates state
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  // AI Voice Input state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Current prompt for variations
  const [currentPrompt, setCurrentPrompt] = useState('');

  const chatEndRef = useRef(null);
  const voiceCommandRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatHistory]);

  // Run simulation with enhanced visualization
  const runSimulation = async (simType) => {
    if (!design || isSimulating) return;
    if (!requireSignedInTool(language === 'tr' ? 'mühendislik simülasyonu' : 'engineering simulation')) return;

    setIsSimulating(true);
    setChatHistory(prev => [...prev, {
      text: language === 'tr' ? `🔬 ${simType} analizi yapılıyor...` : `🔬 Running ${simType} analysis...`,
      isUser: false,
      isSystem: true
    }]);

    try {
      const res = await fetch(`${API_URL}/api/simulation/analyze`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...((localStorage.getItem('session_token') || sessionStorage.getItem('session_token'))
            ? { Authorization: `Bearer ${localStorage.getItem('session_token') || sessionStorage.getItem('session_token')}` }
            : {}),
        },
        body: JSON.stringify({
          design_data: design,
          simulation_type: simType
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSimulationResult(data);
        setShowSimulationModal(true);
        setChatHistory(prev => [...prev, {
          text: language === 'tr'
            ? `✅ ${simType} analizi tamamlandı! Skor: ${data.analysis?.overall_score || 'N/A'}% - Risk: ${data.analysis?.risk_level || 'N/A'}`
            : `✅ ${simType} analysis complete! Score: ${data.analysis?.overall_score || 'N/A'}% - Risk: ${data.analysis?.risk_level || 'N/A'}`,
          isUser: false,
          isSystem: true
        }]);
      } else {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.detail?.message || payload?.detail || `HTTP ${res.status}`);
      }
    } catch (err) {
      console.error('Simulation error:', err);
      setChatHistory(prev => [...prev, {
        text: language === 'tr' ? '❌ Simülasyon hatası' : '❌ Simulation error',
        isUser: false
      }]);
    } finally {
      setIsSimulating(false);
    }
  };

  // Generate schematic assembly-sequence animation
  const generateExplodedView = async () => {
    if (!design || isGeneratingExploded) return;
    if (!requireSignedInTool(language === 'tr' ? 'montaj sırası animasyonu' : 'assembly sequence animation')) return;

    setIsGeneratingExploded(true);
    setChatHistory(prev => [...prev, {
      text: language === 'tr' ? '🎬 Montaj sırası animasyonu hazırlanıyor...' : '🎬 Preparing schematic assembly sequence...',
      isUser: false,
      isSystem: true
    }]);

    try {
      const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/animation/exploded-view`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(storedToken ? { Authorization: `Bearer ${storedToken}` } : {}),
        },
        body: JSON.stringify({
          design_data: design,
          explosion_factor: 1.5,
          animation_duration: 3.0
        })
      });

      if (res.ok) {
        const data = await res.json();
        setExplodedViewData(data);
        setShowExplodedModal(true);
        setIsExplodedPlaying(true);
        setChatHistory(prev => [...prev, {
          text: language === 'tr'
            ? `✅ Montaj sırası animasyonu hazır! ${data.components?.length || 0} bileşen analiz edildi.`
            : `✅ Assembly sequence animation ready! ${data.components?.length || 0} components analyzed.`,
          isUser: false,
          isSystem: true
        }]);
      } else {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.detail?.message || payload?.detail || `HTTP ${res.status}`);
      }
    } catch (err) {
      console.error('Exploded view error:', err);
      setChatHistory(prev => [...prev, {
        text: language === 'tr' ? '❌ Animasyon hatası' : '❌ Animation error',
        isUser: false
      }]);
    } finally {
      setIsGeneratingExploded(false);
    }
  };

  // Generate Prototype Presentation
  const generatePresentation = async () => {
    if (!design || isGeneratingPresentation) return;
    if (!requireSignedInTool(language === 'tr' ? 'prototip sunumu' : 'prototype presentation')) return;

    setIsGeneratingPresentation(true);
    setChatHistory(prev => [...prev, {
      text: language === 'tr' ? '📊 Prototip sunum şeması oluşturuluyor...' : '📊 Generating prototype presentation...',
      isUser: false,
      isSystem: true
    }]);

    try {
      const res = await fetch(`${API_URL}/api/design/prototype-presentation`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...((localStorage.getItem('session_token') || sessionStorage.getItem('session_token'))
            ? { Authorization: `Bearer ${localStorage.getItem('session_token') || sessionStorage.getItem('session_token')}` }
            : {}),
        },
        body: JSON.stringify({
          design_data: design,
          prompt: currentPrompt,
          language
        })
      });

      if (res.ok) {
        const data = await res.json();
        setPresentation(data.presentation);
        setShowPresentation(true);
        setChatHistory(prev => [...prev, {
          text: language === 'tr'
            ? '✅ Prototip sunum şeması hazır! Detayları görmek için "Sunumu Görüntüle" butonuna tıklayın.'
            : '✅ Prototype presentation ready! Click "View Presentation" to see details.',
          isUser: false,
          isSystem: true
        }]);
      } else {
        throw new Error('Presentation generation failed');
      }
    } catch (err) {
      console.error('Presentation error:', err);
      setChatHistory(prev => [...prev, {
        text: language === 'tr' ? '❌ Sunum oluşturulamadı.' : '❌ Presentation generation failed.',
        isUser: false
      }]);
    } finally {
      setIsGeneratingPresentation(false);
    }
  };

  // Generate 3D Prototype with Meshy AI
  const generate3DPrototype = async () => {
    if (!design || isGenerating3D) return;
    if (!requireSignedInTool(language === 'tr' ? '3D prototip' : '3D prototype')) return;

    setIsGenerating3D(true);
    setMeshyStatus('STARTING');
    setMeshyProgress(0);

    setChatHistory(prev => [...prev, {
      text: language === 'tr' ? '🚀 3D prototip oluşturuluyor... Bu işlem 2-3 dakika sürebilir.' : '🚀 Generating 3D prototype... This may take 2-3 minutes.',
      isUser: false,
      isSystem: true
    }]);

    try {
      const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const authHeaders = {
        'Content-Type': 'application/json',
        ...(storedToken ? { Authorization: `Bearer ${storedToken}` } : {}),
      };
      // Call the full prototype endpoint
      const res = await fetch(`${API_URL}/api/design/generate-3d-from-design`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: JSON.stringify({
          design_data: design,
          prompt: currentPrompt,
          language
        })
      });

      if (res.ok) {
        const data = await res.json();
        setMeshyTaskId(data.meshy_task_id);
        setMeshyStatus('PENDING');

        setChatHistory(prev => [...prev, {
          text: language === 'tr'
            ? `✅ 3D model oluşturma başlatıldı! Task ID: ${data.meshy_task_id}`
            : `✅ 3D model generation started! Task ID: ${data.meshy_task_id}`,
          isUser: false,
          isSystem: true
        }]);

        // Start polling for status
        if (data.meshy_task_id) {
          pollMeshyStatus(data.meshy_task_id, storedToken);
        }
      } else {
        throw new Error('Failed to start 3D generation');
      }
    } catch (err) {
      console.error('3D Prototype error:', err);
      setChatHistory(prev => [...prev, {
        text: language === 'tr' ? '❌ 3D prototip oluşturulamadı.' : '❌ 3D prototype generation failed.',
        isUser: false
      }]);
      setMeshyStatus('FAILED');
    } finally {
      setIsGenerating3D(false);
    }
  };

  // Poll Meshy AI for task status
  const pollMeshyStatus = async (taskId, sessionToken = null) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max (5 sec intervals)

    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/meshy/status/${taskId}`, {
          credentials: 'include',
          headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setMeshyStatus(data.status);
          setMeshyProgress(data.progress || 0);

          if (data.status === 'SUCCEEDED') {
            // Get the 3D model URL - check for non-empty string
            const glbUrl = data.model_urls?.glb;
            if (glbUrl && glbUrl.length > 0) {
              // Use proxy to avoid CORS issues
              const proxyUrl = `${API_URL}/api/proxy/model?url=${encodeURIComponent(glbUrl)}`;
              setModel3DUrl(proxyUrl);
              setRawGlbUrl(glbUrl);
              setShow3DViewer(true);
              setChatHistory(prev => [...prev, {
                text: language === 'tr'
                  ? '🎉 3D prototip hazır! Modeli görüntülemek için "3D Görüntüle" butonuna tıklayın.'
                  : '🎉 3D prototype ready! Click "View 3D" button to see the model.',
                isUser: false,
                isSystem: true
              }]);
            } else {
              setChatHistory(prev => [...prev, {
                text: language === 'tr' ? '⚠️ 3D model URL\'si alınamadı.' : '⚠️ Could not get 3D model URL.',
                isUser: false
              }]);
            }
            return; // Stop polling
          } else if (data.status === 'FAILED' || data.status === 'EXPIRED') {
            setChatHistory(prev => [...prev, {
              text: language === 'tr' ? '❌ 3D model oluşturma başarısız oldu.' : '❌ 3D model generation failed.',
              isUser: false
            }]);
            return; // Stop polling
          } else if (data.status === 'PENDING' || data.status === 'IN_PROGRESS') {
            // Continue polling
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 5000); // Check every 5 seconds
            } else {
              setChatHistory(prev => [...prev, {
                text: language === 'tr' ? '⏰ 3D model hala işleniyor. Daha sonra kontrol edin.' : '⏰ 3D model still processing. Check back later.',
                isUser: false
              }]);
            }
          }
        }
      } catch (err) {
        console.error('Meshy status check error:', err);
      }
    };

    checkStatus();
  };

  // Create real CAD document in Onshape
  const createOnshapeCAD = async () => {
    const designData = proProject?.project || design?.design_specs || design;
    const activeModelUrl = mesh3dModelUrl || model3DUrl;

    console.log('createOnshapeCAD called', {
      hasConceptImage: !!(proProject?.concept_image || conceptImage),
      hasDesign: !!(proProject?.project || design),
      hasDesignData: !!designData,
      has3DModel: !!activeModelUrl
    });

    if (!requireSignedInTool('Onshape CAD')) return;
    if (!proProject?.project_id) {
      const msg = language === 'tr'
        ? '⚠️ Onshape ve üretim CAD dışa aktarımı için önce Pro modunda bir mühendislik projesi oluşturun.'
        : '⚠️ Create an engineering project in Pro mode before using Onshape or production CAD export.';
      setChatHistory(prev => [...prev, { text: msg, isUser: false }]);
      return;
    }

    setIsCreatingCAD(true);

    // Show appropriate loading message based on whether we have a 3D model
    const loadingMsg = activeModelUrl
      ? (language === 'tr' ? '🔧 3D model Onshape\'e aktarılıyor... Bu işlem 1-2 dakika sürebilir.' : '🔧 Importing 3D model to Onshape... This may take 1-2 minutes.')
      : (language === 'tr' ? '🔧 Onshape\'te CAD dokümanı oluşturuluyor...' : '🔧 Creating CAD document in Onshape...');

    setChatHistory(prev => [...prev, { text: loadingMsg, isUser: false }]);

    try {
      // Extract dimensions from design_specs
      const dimensions = designData?.dimensions || {};
      const designSpecs = {
        dimensions: {
          width: parseInt(dimensions.width) || 200,
          height: parseInt(dimensions.height) || 100,
          depth: parseInt(dimensions.depth) || 150
        },
        material: designData?.materials?.[0] || 'aluminum',
        shape: designData?.type || 'box'
      };

      const designName = designData?.name || currentPrompt?.slice(0, 50) || 'AI Design';

      // Get the actual GLB URL from the proxy URL if exists
      let actualModelUrl = null;
      if (activeModelUrl) {
        // model3DUrl might be a proxy URL like /api/proxy/model?url=...
        // Extract the real URL from it
        if (activeModelUrl.includes('/api/proxy/model?url=')) {
          const urlParam = new URL(activeModelUrl, window.location.origin);
          actualModelUrl = urlParam.searchParams.get('url');
        } else if (activeModelUrl.startsWith('http')) {
          actualModelUrl = activeModelUrl;
        }
      }

      console.log('Sending to Onshape:', {
        design_name: designName,
        designSpecs,
        model_url: actualModelUrl
      });

      const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const callOnshape = (isPublic) => fetch(`${API_URL}/api/onshape/from-ai-design`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(storedToken ? { Authorization: `Bearer ${storedToken}` } : {}),
        },
        body: JSON.stringify({
          project_id: proProject?.project_id,
          design_name: designName,
          design_specs: designSpecs,
          model_url: actualModelUrl,
          language: language,
          is_public: isPublic
        })
      });

      let response = await callOnshape(false);

      // Entitlement fallback — free Onshape plans can't create private documents.
      if (!response.ok && (response.status === 409 || response.status === 403)) {
        const errPreview = await response.clone().text();
        if (/private document|paid onshape plan|not authorized/i.test(errPreview) || response.status === 409) {
          setChatHistory(prev => [...prev, {
            text: language === 'tr'
              ? 'ℹ️ Onshape planınız özel doküman oluşturmayı desteklemiyor — public doküman olarak yeniden deneniyor...'
              : 'ℹ️ Your Onshape plan does not allow private documents — retrying as a public document...',
            isUser: false
          }]);
          response = await callOnshape(true);
        }
      }

      console.log('Onshape response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Onshape success:', data);
        setCadDocument(data);
        setShowCADSuccess(true);

        // Show different success message based on whether model was imported
        const successMsg = data.model_imported
          ? (language === 'tr'
              ? `✅ 3D model başarıyla Onshape'e aktarıldı! Gerçek CAD dokümanınız hazır.`
              : `✅ 3D model successfully imported to Onshape! Your real CAD document is ready.`)
          : (language === 'tr'
              ? `✅ CAD dokümanı oluşturuldu! Onshape'te açmak için butona tıklayın.`
              : `✅ CAD document created! Click the button to open in Onshape.`);

        setChatHistory(prev => [...prev, { text: successMsg, isUser: false }]);

        // Add import details if available
        if (data.import_details && data.import_details.length > 0) {
          setChatHistory(prev => [...prev, {
            text: `ℹ️ ${data.import_details}`,
            isUser: false,
            isSystem: true
          }]);
        }
      } else {
        const errorText = await response.text();
        console.error('Onshape error response:', errorText);
        throw new Error(`Failed to create CAD document: ${errorText}`);
      }
    } catch (err) {
      console.error('Onshape CAD error:', err);
      setChatHistory(prev => [...prev, {
        text: language === 'tr'
          ? `❌ Onshape aktarımı başarısız oldu: ${err.message}\n\n💡 Alternatif: 3D modelinizi "CAD Dışa Aktar" bölümünden STEP / IGES / STL formatında indirip Onshape, Fusion 360 veya SolidWorks'e elle içe aktarabilirsiniz. (Not: Ücretsiz Onshape planları API ile özel doküman oluşturmayı desteklemeyebilir.)`
          : `❌ Onshape transfer failed: ${err.message}\n\n💡 Alternative: download your 3D model as STEP / IGES / STL from the "CAD Export" section and import it manually into Onshape, Fusion 360 or SolidWorks. (Note: free Onshape plans may not support API document creation.)`,
        isUser: false
      }]);
    } finally {
      setIsCreatingCAD(false);
    }
  };

  const downloadProGlb = async () => {
    if (!mesh3dModelUrl || isExportingCad) return;
    setIsExportingCad('glb');
    try {
      const response = await fetch(`${API_URL}/api/proxy/model?url=${encodeURIComponent(mesh3dModelUrl)}`);
      if (!response.ok) throw new Error(`GLB download failed (HTTP ${response.status})`);
      const blob = await response.blob();
      await validateDownload(blob, 'glb', language);
      triggerBlobDownload(blob, `project_${proProject?.project_id || 'model'}.glb`);
    } catch (error) {
      setChatHistory(previous => [...previous, { text: error.message, isUser: false, isSystem: true }]);
    } finally {
      setIsExportingCad(null);
    }
  };

  const exportCadFile = async (format, modelUrl = null) => {
    if (!requireSignedInTool('CAD export')) return;
    if (!proProject?.project_id) {
      setChatHistory(prev => [...prev, {
        text: language === 'tr'
          ? '⚠️ CAD dosyaları yalnızca doğrulanabilir bir Pro proje kimliği üzerinden oluşturulur. Önce Pro projesini tamamlayın.'
          : '⚠️ CAD files require a verifiable Pro project. Complete the Pro project first.',
        isUser: false,
        isSystem: true,
      }]);
      return;
    }
    const needsModel = format !== 'dxf';
    if (needsModel && !modelUrl) {
      setChatHistory(prev => [...prev, {
        text: language === 'tr'
          ? `⚠️ ${format.toUpperCase()} için önce döndürülebilir 3D prototipi tamamlayın.`
          : `⚠️ Complete the rotatable 3D prototype before exporting ${format.toUpperCase()}.`,
        isUser: false,
      }]);
      return;
    }

    try {
      setIsExportingCad(format);
      const exportDesign = proProject.project || {};
      const body = {
        project_id: proProject.project_id,
        format,
        design_name: exportDesign?.project_summary?.title || exportDesign?.name || 'nexus-design',
      };
      if (needsModel) body.model_url = modelUrl;
      if (format === 'dxf') body.spec = exportDesign;
      if (format === 'step' || format === 'iges') body.max_faces = 8000;
      const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/cad/export`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(storedToken ? { Authorization: `Bearer ${storedToken}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.detail?.message || payload?.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      await validateDownload(blob, format, language);
      triggerBlobDownload(blob, `${body.design_name}.${format}`);
      setChatHistory(prev => [...prev, {
        text: language === 'tr'
          ? `✅ ${format.toUpperCase()} dosyası indirildi.`
          : `✅ ${format.toUpperCase()} file downloaded.`,
        isUser: false,
      }]);
    } catch (err) {
      setChatHistory(prev => [...prev, {
        text: language === 'tr'
          ? `❌ ${format.toUpperCase()} dışa aktarımı başarısız: ${err.message}`
          : `❌ ${format.toUpperCase()} export failed: ${err.message}`,
        isUser: false,
      }]);
    } finally {
      setIsExportingCad(null);
    }
  };

  // AI Voice Input — opens the Voice Studio v2 overlay (live waveform + review).
  // The old inline implementation has been replaced by VoiceStudioOverlay which
  // gives the user a chance to see/edit what was transcribed BEFORE it lands
  // in the prompt — a big UX upgrade for prompt fidelity.
  const toggleVoice = async () => {
    window.dispatchEvent(new CustomEvent('nexus:open-voice', {
      detail: { studioMode },
    }));
  };

  // Handle template selection
  const handleTemplateSelect = async (template) => {
    setChatMessage(template.prompt);
    // Track usage
    try {
      await fetch(`${API_URL}/api/templates/${template.template_id}/use`, { method: 'POST' });
    } catch (err) {
      console.error('Template usage track error:', err);
    }
    setChatHistory(prev => [...prev, {
      text: language === 'tr' ? `📋 Şablon seçildi: ${template.name}` : `📋 Template selected: ${template.name}`,
      isUser: false,
      isSystem: true
    }]);
  };

  // State for PDF export
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // Export presentation as PDF
  const exportPresentationPdf = async () => {
    if (!presentation || isExportingPdf) return;

    setIsExportingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/export/presentation-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presentation,
          design_name: design?.design_specs?.name || 'Nexus Design',
          language,
          concept_image: conceptImage
        })
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.detail || `PDF export failed (HTTP ${response.status})`);
      }
      const blob = await response.blob();
      await validateDownload(blob, 'pdf', language);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nexus-presentation-${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('PDF export error:', err);
      setChatHistory(prev => [...prev, {
        text: language === 'tr' ? '❌ PDF oluşturulurken hata oluştu' : '❌ Error creating PDF',
        isUser: false
      }]);
    } finally {
      setIsExportingPdf(false);
    }
  };

  // Clear all
  const clearAll = () => {
    setDesign(null);
    setConceptImage(null);
    setChatHistory([]);
    setCurrentPrompt('');
    setSimulationResult(null);
    setShowSimulationModal(false);
    setExplodedViewData(null);
    setShowExplodedModal(false);
    setIsExplodedPlaying(false);
    setPresentation(null);
    setShowPresentation(false);
    setShowShareModal(false);
    setShowTemplatesModal(false);
  };

  // Export presentation as JSON
  const exportPresentation = () => {
    if (!presentation) return;
    const data = {
      presentation,
      design,
      conceptImage,
      prompt: currentPrompt,
      exportedAt: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nexus-presentation-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Handle chat/design through the always-on AI design copilot
  const handleChat = async () => {
    const voiceRequest = voiceCommandRef.current;
    const overrideMessage = typeof voiceRequest === 'string' ? voiceRequest : voiceRequest?.prompt;
    const requestedMode = typeof voiceRequest === 'object' ? voiceRequest?.mode : null;
    const forceDesignGeneration = requestedMode === 'create' || requestedMode === 'improve';
    const requestedMessage = typeof overrideMessage === 'string' ? overrideMessage : chatMessage;
    if (!requestedMessage.trim() || !quotaReady) return;
    const revisionCommand = requestedMode === 'improve' || (!!design && /\b(değiştir|iyileştir|küçült|büyüt|ekle|çıkar|kaldır|güçlendir|hafiflet|öne al|arkaya al|konumlandır|move|change|improve|resize|add|remove|strengthen|lighten|reposition)\b/i.test(requestedMessage));

    // Fail fast before the copilot or any paid generation call.
    if (!canCreateDesign()) {
      setShowUpgradeModal(true);
      setChatHistory(prev => [...prev, {
        text: language === 'tr'
          ? `⚠️ Tasarım limitinize ulaştınız (${designCount}/${designLimit}). Devam etmek için giriş yapın veya planınızı yükseltin.`
          : `⚠️ You've reached your design limit (${designCount}/${designLimit}). Sign in or upgrade your plan to continue.`,
        isUser: false,
      }]);
      return;
    }

    let msg = requestedMessage.trim();
    setChatMessage('');
    setChatHistory(prev => [...prev, { text: msg, isUser: true }]);

    // Every message first goes through the stateful design copilot. It remembers
    // earlier answers, asks only critical questions, applies sensible defaults,
    // and starts generation automatically when the brief is ready.
    setIsChatting(true);
    try {
      const copilotRes = await fetch(API_URL + '/api/design/copilot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          session_id: sessionId,
          language,
          project_type: proProjectType,
          studio_mode: studioMode,
          design_context: design
        })
      });
      if (!copilotRes.ok) throw new Error('Copilot error');

      const copilotData = await copilotRes.json();
      if (copilotData.response) {
        let copilotReply = copilotData.response;
        const fields = Array.isArray(copilotData.missing_fields)
          ? copilotData.missing_fields.filter(Boolean).slice(0, 8)
          : [];
        if (
          copilotData.status === 'clarifying'
          && fields.length
          && !/(^|\n)\s*1[.)]/.test(copilotReply)
        ) {
          const heading = language === 'tr' ? 'Devam edebilmem için:' : 'To continue, please answer:';
          copilotReply += `\n\n${heading}\n${fields.map((field, index) => `${index + 1}. ${field}?`).join('\n')}`;
        }
        setChatHistory(prev => [...prev, {
          text: copilotReply,
          isUser: false,
          isSystem: copilotData.status === 'ready'
        }]);
      }

      if ((copilotData.status !== 'ready' || !copilotData.compiled_prompt) && !revisionCommand && !forceDesignGeneration) {
        return;
      }

      if (copilotData.compiled_prompt) msg = copilotData.compiled_prompt;
    } catch (copilotError) {
      console.warn('Design copilot unavailable; using direct intent flow:', copilotError);
    } finally {
      setIsChatting(false);
    }

    // The input asks users to describe a product idea, so a noun-led brief must
    // not require an explicit "design" verb before entering the design pipeline.
    const normalizedMessage = msg.trim().toLowerCase();
    const designKeywords = [
      'tasarla', 'tasarım', 'oluştur', 'üret', 'yap',
      'design', 'create', 'generate', 'make', 'build', 'prototype',
      'concept image', 'konsept görsel',
    ];
    const questionPatterns = ['nasıl', 'neden', 'ne kadar', 'how', 'why', 'what', 'which', '?'];
    const greetingPattern = /^(merhaba|selam|hey|hello|hi|thanks|thank you|teşekkürler)[!.\s]*$/i;
    const hasDesignVerb = designKeywords.some(keyword => normalizedMessage.includes(keyword));
    const isQuestion = questionPatterns.some(q => normalizedMessage.includes(q));
    const isProductBrief = !design && normalizedMessage.split(/\s+/).length >= 4 && !isQuestion && !greetingPattern.test(normalizedMessage);
    let isDesignRequest = forceDesignGeneration || hasDesignVerb || isProductBrief;
    let detectedIntent = null;
    let isRevision = revisionCommand;
    try {
      const intentRes = await fetch(API_URL + '/api/design/detect-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: requestedMessage,
          language,
          has_active_design: !!design
        })
      });

      if (intentRes.ok) {
        const intentData = await intentRes.json();
        detectedIntent = intentData.intent;
        isDesignRequest = isDesignRequest || (intentData.intent === 'design' && intentData.confidence >= 0.6);

        // A modification is a new revision of the active design, not plain chat.
        if (intentData.intent === 'modify' && design) {
          isRevision = true;
          isDesignRequest = true;
        }
      }
    } catch (err) {
      // Fallback to keyword-based detection
      isDesignRequest = isRevision || ((hasDesignVerb || isProductBrief) && !isQuestion);
    }

    // Safety net — explicit generation verbs must never fall through to plain chat,
    // even if the classifier mislabels them (prod bug: "görsel üret" answered as chat).
    if (!isDesignRequest && !(detectedIntent === 'modify' && design)) {
      const lower = msg.toLowerCase();
      const strongVerbs = ['tasarla', 'tasarım', 'oluştur', 'üret', 'çiz', 'görsel',
        'prototip', 'design', 'create', 'generate', 'draw', 'render', 'sketch',
        'build me', 'make me', 'image of'];
      const questionish = /(\bnasıl\b|\bneden\b|\bnedir\b|\bne kadar\b|\bhow\b|\bwhy\b|\bwhat\b|\bwhich\b)/i.test(lower);
      if (!questionish && strongVerbs.some((k) => lower.includes(k))) {
        isDesignRequest = true;
      }
    }

    if (isDesignRequest) {
      // Check design limit before generating
      if (!canCreateDesign()) {
        setShowUpgradeModal(true);
        setChatHistory(prev => [...prev, {
          text: language === 'tr'
            ? '⚠️ Tasarım limitinize ulaştınız. Daha fazla tasarım için planınızı yükseltin.'
            : '⚠️ You\'ve reached your design limit. Upgrade your plan for more designs.',
          isUser: false
        }]);
        return;
      }

      // A Standard generation starts a new artifact lineage. Never leave an
      // unrelated Pro package visible underneath the new concept.
      if (!isRevision) {
        setProProject(null);
        setShowProProjectResult(false);
      }
      setIsGenerating(true);
      setConceptImage(null);

      setChatHistory(prev => [...prev, {
        text: language === 'tr'
          ? (isRevision ? '🔧 Tasarım iyileştirmesi uygulanıyor...' : '🔧 AI tasarım oluşturuluyor...')
          : (isRevision ? '🔧 Applying the design improvement...' : '🔧 Generating AI design...'),
        isUser: false,
        isSystem: true
      }]);

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 min timeout for design

        // Step 1: Generate design specs first (faster, ~30-60 sec)
        setChatHistory(prev => [...prev, {
          text: language === 'tr' ? '⚙️ Tasarım özellikleri hesaplanıyor...' : '⚙️ Calculating design specifications...',
          isUser: false,
          isSystem: true
        }]);

        const storedToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
        const authenticatedHeaders = {
          'Content-Type': 'application/json',
          ...(storedToken ? { Authorization: `Bearer ${storedToken}` } : {}),
        };
        const res = await fetch(API_URL + '/api/design/generate', {
          method: 'POST',
          credentials: 'include',
          headers: authenticatedHeaders,
          body: JSON.stringify({
            prompt: msg,
            language,
            session_id: sessionId,
            design_context: isRevision ? design : undefined,
          }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          const detail = errorData?.detail || {};
          if (res.status === 429 && /quota_exceeded/.test(detail?.code || '')) {
            if (Number.isFinite(detail.used)) setDesignCount(detail.used);
            if (Number.isFinite(detail.limit)) setDesignLimit(detail.limit);
            setShowUpgradeModal(true);
            setChatHistory(prev => [...prev, {
              text: language === 'tr'
                ? `⚠️ Tasarım limitinize ulaştınız (${detail.used}/${detail.limit}). Daha fazla tasarım için planınızı yükseltin.`
                : `⚠️ You've reached your design limit (${detail.used}/${detail.limit}). Upgrade your plan for more designs.`,
              isUser: false,
            }]);
            return;
          }
          throw new Error(typeof detail === 'string' ? detail : (detail.message || 'API error'));
        }
        const data = await res.json();

        setDesign(data.design);
        setCurrentPrompt(msg);

        // Trust the server-side quota counter; demo fallback remains local-only.
        if (Number.isFinite(data?.usage?.used)) {
          setDesignCount(data.usage.used);
          if (Number.isFinite(data?.usage?.limit)) {
            setDesignLimit(data.usage.limit === -1 ? 9999 : data.usage.limit);
          }
        } else {
          incrementDesignCount();
        }

        const analysis = data.design?.analysis || '';
        if (analysis) {
          setChatHistory(prev => [...prev, { text: analysis, isUser: false }]);
        }

        setChatHistory(prev => [...prev, {
          text: language === 'tr'
            ? '✅ Tasarım oluşturuldu! Şimdi 3D model için "3D Modele Dönüştür" butonunu kullanabilirsiniz.'
            : '✅ Design created! You can now use "Convert to 3D" button to generate 3D model.',
          isUser: false,
          isSystem: true
        }]);

        // Step 2: Try to generate the optional concept image in the background.
        // Keep one status row and always resolve it so the UI never looks stuck.
        const imageStatusText = language === 'tr'
          ? '🎨 Konsept görseli oluşturuluyor (arka planda)...'
          : '🎨 Generating concept image (background)...';
        const setImageStatus = (text) => {
          setChatHistory(prev => {
            const next = [...prev];
            for (let i = next.length - 1; i >= 0; i -= 1) {
              if (next[i]?.isSystem && next[i]?.text === imageStatusText) {
                next[i] = { ...next[i], text };
                return next;
              }
            }
            return [...next, { text, isUser: false, isSystem: true }];
          });
        };

        setChatHistory(prev => [...prev, {
          text: imageStatusText,
          isUser: false,
          isSystem: true
        }]);

        const imageController = new AbortController();
        const imageTimeoutId = setTimeout(() => imageController.abort(), 50000);

        try {
          const imageRes = await fetch(API_URL + '/api/design/concept-image', {
            method: 'POST',
            credentials: 'include',
            headers: {
              ...authenticatedHeaders,
              ...(data?.capability_token
                ? { 'X-Design-Capability': data.capability_token }
                : {}),
            },
            body: JSON.stringify({
              design_name: data.design?.design_specs?.name || msg,
              session_id: sessionId,
              design_id: data.design_id,
              design_data: data.design || null,
            }),
            signal: imageController.signal
          });

          if (!imageRes.ok) {
            const imageError = await imageRes.json().catch(() => ({}));
            const detail = imageError?.detail;
            throw new Error(
              typeof detail === 'string'
                ? detail
                : (detail?.message || `Concept image request failed (HTTP ${imageRes.status})`)
            );
          }

          const imageData = await imageRes.json();
          if (imageData.concept_image) {
            setConceptImage(imageData.concept_image);
            setImageStatus(
              language === 'tr' ? '✅ Konsept görseli hazır!' : '✅ Concept image ready!'
            );
          } else {
            setImageStatus(
              language === 'tr'
                ? '⚠️ Konsept görseli şu anda kullanılamıyor. Teknik tasarımınız hazır; daha sonra tekrar deneyin.'
                : '⚠️ Concept image is temporarily unavailable. Your technical design is ready; try again later.'
            );
          }
        } catch (imgErr) {
          console.warn('Concept image unavailable:', imgErr.message);
          const timedOut = imgErr?.name === 'AbortError' || /abort|timeout/i.test(imgErr?.message || '');
          setImageStatus(
            language === 'tr'
              ? (timedOut
                  ? '⚠️ Konsept görseli zaman aşımına uğradı. Teknik tasarımınız hazır; daha sonra tekrar deneyin.'
                  : '⚠️ Konsept görseli şu anda kullanılamıyor. Teknik tasarımınız hazır; daha sonra tekrar deneyin.')
              : (timedOut
                  ? '⚠️ Concept image timed out. Your technical design is ready; try again later.'
                  : '⚠️ Concept image is temporarily unavailable. Your technical design is ready; try again later.')
          );
        } finally {
          clearTimeout(imageTimeoutId);
        }

      } catch (err) {
        console.error('Design error:', err);

        // Provide more helpful error messages
        let errorMsg = err.message;
        if (err.name === 'AbortError' || err.message.includes('abort')) {
          errorMsg = language === 'tr'
            ? 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.'
            : 'Request timed out. Please try again.';
        } else if (err.message === 'Load failed' || err.message === 'Failed to fetch') {
          errorMsg = language === 'tr'
            ? 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edip tekrar deneyin.'
            : 'Connection error. Please check your internet and try again.';
        }

        setChatHistory(prev => [...prev, {
          text: `❌ ${errorMsg}`,
          isUser: false
        }]);
      } finally {
        setIsGenerating(false);
      }
    } else {
      // Regular chat - ChatGPT style conversation
      setIsChatting(true);
      try {
        const res = await fetch(API_URL + '/api/design/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: msg,
            session_id: sessionId,
            language,
            design_context: design // Pass current design for context
          })
        });
        if (!res.ok) throw new Error('Chat error');
        const data = await res.json();
        setChatHistory(prev => [...prev, { text: data.response, isUser: false }]);
      } catch (err) {
        console.error('Chat error:', err);
        setChatHistory(prev => [...prev, {
          text: language === 'tr' ? 'Bir hata oluştu. Lütfen tekrar deneyin.' : 'An error occurred. Please try again.',
          isUser: false
        }]);
      } finally {
        setIsChatting(false);
      }
    }
  };

  const handleVoiceDesignRequest = async (prompt, options = {}) => {
    const cleanPrompt = typeof prompt === 'string' ? prompt.trim() : '';
    if (!cleanPrompt) return;

    const requestedStudioMode = options?.studioMode || studioMode;
    if (requestedStudioMode === 'pro') {
      setStudioMode('pro');
      const isImprovement = options?.mode === 'improve' && (proProject || lastOriginalPrompt);
      const projectPrompt = isImprovement
        ? `${lastOriginalPrompt || 'Existing Pro project'}\n\nRequested revision: ${cleanPrompt}`
        : cleanPrompt;
      setChatHistory(prev => [...prev, {
        text: cleanPrompt,
        isUser: true,
      }, {
        text: language === 'tr'
          ? (isImprovement ? '🔧 Pro proje iyileştirmesi başlatılıyor…' : '🚀 Pro proje oluşturuluyor…')
          : (isImprovement ? '🔧 Starting the Pro project revision…' : '🚀 Starting the Pro project…'),
        isUser: false,
        isSystem: true,
      }]);
      await generateProProject(null, projectPrompt);
      return;
    }

    setStudioMode('standard');
    voiceCommandRef.current = { prompt: cleanPrompt, mode: options?.mode || 'create' };
    try {
      await handleChat();
    } finally {
      voiceCommandRef.current = null;
    }
  };

  // Global Nexus keeps the realtime session mounted outside this page. It hands
  // design commands back to the studio through a bounded, one-shot event or a
  // sessionStorage handoff after route navigation.
  useEffect(() => {
    if (!quotaReady) return undefined;

    const acceptVoiceRequest = (payload) => {
      if (!payload?.prompt) return;
      window.setTimeout(() => {
        void handleVoiceDesignRequest(payload.prompt, {
          mode: payload.mode || 'create',
          studioMode: payload.studioMode || 'standard',
        });
      }, 0);
    };
    const onVoiceRequest = (event) => {
      sessionStorage.removeItem('nexus-voice-design-request');
      acceptVoiceRequest(event.detail);
    };
    window.addEventListener('nexus:voice-design-request', onVoiceRequest);

    try {
      const raw = sessionStorage.getItem('nexus-voice-design-request');
      if (raw) {
        sessionStorage.removeItem('nexus-voice-design-request');
        acceptVoiceRequest(JSON.parse(raw));
      }
    } catch {
      sessionStorage.removeItem('nexus-voice-design-request');
    }

    return () => window.removeEventListener('nexus:voice-design-request', onVoiceRequest);
  }, [quotaReady, studioMode, proProject?.project_id, lastOriginalPrompt]);

  const handleExport = () => {
    const data = { design, conceptImage, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    triggerBlobDownload(blob, 'nexus-design-' + Date.now() + '.json');
  };

  const t = language === 'tr' ? {
    title: 'AI Konsept Tasarım Stüdyosu',
    subtitle: 'Fikirlerinizi yüksek kaliteli konsept görsellere dönüştürün',
    placeholder: 'Ürünü, kullanıcıyı, ortamı, boyutu, gücü ve zorunlu özellikleri anlatın…',
    aiReady: 'Prototip yardımcı pilotu hazır — gereksinimden 2B/3B, kod ve sunuma kadar ilerleyelim.',
    viewer: 'AI Konsept Görsel',
    specs: 'Teknik Özellikler',
    code: 'Kod',
    bom: 'Malzeme Listesi',
    features: 'Özellikler',
    simulation: 'Simülasyon',
    stress: 'Stres Analizi',
    thermal: 'Termal Analiz',
    weight: 'Ağırlık Analizi',
    // Pro Project translations
    standardMode: 'Standart',
    proMode: 'Pro Proje',
    proModeDesc: 'Tam proje paketi oluşturun: Sunum, teknik döküman, malzeme listesi, maliyet analizi',
    projectType: 'Proje Türü',
    types: { product: 'Ürün', software: 'Yazılım', robot: 'Robot', vehicle: 'Araç', building: 'Bina', other: 'Diğer' },
    includeCode: 'Kod üret (yazılım projeleri için)',
    generateProject: 'Proje Oluştur',
    generating: 'Proje oluşturuluyor... (1-2 dk)',
    projectReady: 'Proje Oluşturuldu · İnceleme Gerekli',
    summary: 'Özet',
    techSpecs: 'Teknik Özellikler',
    materials: 'Malzemeler',
    costs: 'Maliyet Analizi',
    manufacturing: 'Üretim Planı',
    market: 'Pazar Analizi',
    downloadJson: 'JSON İndir',
    downloadPdf: 'PDF İndir',
    projectTitle: 'Proje Başlığı',
    targetMarket: 'Hedef Pazar',
    problemSolved: 'Çözülen Problem',
    uniqueValue: 'Benzersiz Değer',
    totalCost: 'Toplam Maliyet',
    retailPrice: 'Satış Fiyatı',
    profitMargin: 'Kar Marjı',
  } : {
    title: 'AI Concept Design Studio',
    subtitle: 'Transform your ideas into high-quality concept images',
    placeholder: 'Describe the product, user, environment, size, power and must-have features…',
    aiReady: 'Your prototype copilot is ready — from requirements through 2D/3D, code and presentation.',
    viewer: 'AI Concept Image',
    specs: 'Technical Specs',
    code: 'Code',
    bom: 'Bill of Materials',
    features: 'Features',
    simulation: 'Simulation',
    stress: 'Stress Analysis',
    thermal: 'Thermal Analysis',
    weight: 'Weight Analysis',
    // Pro Project translations
    standardMode: 'Standard',
    proMode: 'Pro Project',
    proModeDesc: 'Create full project package: Presentation, technical docs, materials list, cost analysis',
    projectType: 'Project Type',
    types: { product: 'Product', software: 'Software', robot: 'Robot', vehicle: 'Vehicle', building: 'Building', other: 'Other' },
    includeCode: 'Generate code (for software projects)',
    generateProject: 'Generate Project',
    generating: 'Generating project... (1-2 min)',
    projectReady: 'Project Generated · Review Required',
    summary: 'Summary',
    techSpecs: 'Technical Specs',
    materials: 'Materials',
    costs: 'Cost Analysis',
    manufacturing: 'Manufacturing Plan',
    market: 'Market Analysis',
    downloadJson: 'Download JSON',
    downloadPdf: 'Download PDF',
    projectTitle: 'Project Title',
    targetMarket: 'Target Market',
    problemSolved: 'Problem Solved',
    uniqueValue: 'Unique Value',
    totalCost: 'Total Cost',
    retailPrice: 'Retail Price',
    profitMargin: 'Profit Margin',
  };

  return (
    <section id="studio" className="py-24 md:py-32 relative bg-[#060710]" data-testid="design-studio" ref={sectionRef}>
      {/* Tutorial Modal */}
      <AnimatePresence>
        {showTutorial && (
          <TutorialModal
            isOpen={showTutorial}
            onClose={() => setShowTutorial(false)}
            language={language}
            onDontShowAgain={handleDontShowTutorialAgain}
          />
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-12 max-w-3xl">
          <span className="inline-block px-3 py-1.5 border border-white/10 text-xs font-mono uppercase tracking-wider text-white/50 mb-6">
            {language === 'tr' ? 'Tasarım Stüdyosu' : 'Design Studio'}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.1] mb-4">
            {language === 'tr' ? (
              <>Fikrinizi <span className="font-bold">canlı bir prototipe</span> dönüştürün</>
            ) : (
              <>Turn your idea into a <span className="font-bold">live prototype</span></>
            )}
          </h2>
          <p className="text-white/50 max-w-xl leading-relaxed">{t.subtitle}</p>

          {/* How to Use Button */}
          <button
            onClick={() => setShowTutorial(true)}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wider text-white/60 hover:text-white border border-white/10 hover:border-white/30 transition-colors"
            data-testid="how-to-use-btn"
          >
            <HelpCircle className="w-4 h-4" />
            {language === 'tr' ? 'Nasıl Kullanılır' : 'How to Use'}
          </button>

          {/* Mode Selector */}
          <div className="flex items-center gap-0 mt-8 border border-white/10 w-fit">
            <button
              onClick={() => setStudioMode('standard')}
              className={`px-5 py-2.5 font-medium text-sm transition-colors ${
                studioMode === 'standard'
                  ? 'bg-white text-black'
                  : 'bg-transparent text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid="mode-standard-btn"
            >
              {t.standardMode}
            </button>
            <button
              onClick={() => setStudioMode('pro')}
              className={`px-5 py-2.5 font-medium text-sm transition-colors flex items-center gap-2 border-l border-white/10 ${
                studioMode === 'pro'
                  ? 'bg-white text-black'
                  : 'bg-transparent text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid="mode-pro-btn"
            >
              <Zap className="w-4 h-4" />
              {t.proMode}
            </button>
          </div>
          {studioMode === 'pro' && (
            <p className="text-xs text-amber-400 mt-2">{t.proModeDesc}</p>
          )}
          {studioMode === 'standard' && (
            <motion.button
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => setStudioMode('pro')}
              data-testid="standard-upsell-banner"
              className="group mt-3 w-full flex items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-emerald-500/10 hover:from-amber-500/20 hover:to-emerald-500/20 px-4 py-2.5 text-left transition-all"
            >
              <div className="flex items-start gap-3">
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-1.5 flex-shrink-0">
                  <Zap className="h-3.5 w-3.5 text-amber-300" />
                </div>
                <div>
                  <div className="text-xs font-bold text-amber-200">
                    {language === 'tr'
                      ? 'Ön inceleme paketi için Pro Project\u2019e geç'
                      : 'Switch to Pro Project for a preliminary review package'}
                  </div>
                  <div className="text-[11px] text-white/55 mt-0.5">
                    {language === 'tr'
                      ? 'Kavram görselleri · ön BOM ve maliyet · doğrulamaya bağlı PDF/3B · uzman incelemesi gerekli'
                      : 'Concept visuals · preliminary BOM and cost · validation-gated PDF/3D · expert review required'}
                  </div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-amber-300 group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </motion.button>
          )}
        </motion.div>

        {/* Pro Project Mode UI */}
        {studioMode === 'pro' ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="border border-white/10 bg-[#0A0D1A] p-6 md:p-8"
          >
            {/* Pro Project Input */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">{t.projectType}</label>
                <div className="grid grid-cols-3 gap-px bg-white/10">
                  {Object.entries(t.types).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setProProjectType(key)}
                      className={`px-3 py-2.5 text-xs font-medium transition-colors ${
                        proProjectType === key
                          ? 'bg-white text-black'
                          : 'bg-[#060710] text-white/60 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">{language === 'tr' ? 'Seçenekler' : 'Options'}</label>
                <label className="flex items-center gap-3 p-3 border border-white/10 bg-[#060710] cursor-pointer hover:border-white/20 transition-colors">
                  <input
                    type="checkbox"
                    checked={includeCode}
                    onChange={(e) => setIncludeCode(e.target.checked)}
                    className="w-4 h-4 accent-white"
                  />
                  <span className="text-white/80 text-xs">{t.includeCode}</span>
                </label>
              </div>
            </div>

            {/* Anonymous Pro jobs use a scoped capability token. Signing in is
                optional for the demo, but preserves projects and raises limits. */}
            {!hasSessionToken() && (
              <div
                data-testid="pro-login-required-notice"
                className="mb-4 flex flex-col gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 sm:flex-row sm:items-center"
              >
                <div className="flex flex-1 items-start gap-2">
                  <span className="mt-0.5 text-amber-300">ⓘ</span>
                  <div className="text-[11px] leading-relaxed text-amber-100">
                    {language === 'tr'
                      ? 'Pro Project\'i ücretsiz demo olarak deneyebilirsiniz. Projenizi hesabınıza kaydetmek ve daha yüksek limitler için giriş yapın.'
                      : 'You can try Pro Project as a free demo. Log in to save the project to your account and unlock higher limits.'}
                  </div>
                </div>
                <a
                  href="/login"
                  className="inline-flex shrink-0 items-center justify-center bg-white px-4 py-2 text-xs font-semibold text-black transition-colors hover:bg-white/90"
                  data-testid="pro-login-cta"
                >
                  {language === 'tr' ? 'Giriş Yap' : 'Log In'} →
                </a>
              </div>
            )}
            {/* Project Prompt Input */}
            <div className="mb-4">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">
                {language === 'tr' ? 'Proje Tanımı' : 'Project Brief'}
              </label>
              <textarea
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder={language === 'tr'
                  ? 'Projenizi detaylı açıklayın... (örn: "Mayın arama robotu tasarla: GPS ile konum takibi, metal dedektörü, uzaktan kumanda, askeri kullanım için")'
                  : 'Describe your project in detail... (e.g., "Design a mine detection robot: GPS tracking, metal detector, remote control, for military use")'}
                className="w-full h-32 px-3 py-2.5 bg-[#060710] border border-white/10 text-white text-sm placeholder-white/30 focus:border-cyan-400/40 focus:outline-none transition-colors font-mono leading-relaxed resize-none"
              />
              <div className="mt-3" data-testid="quick-engineering-constraints">
                <p className="text-[10px] font-mono uppercase tracking-wider text-white/60 mb-2">
                  {language === 'tr' ? 'Hızlı mühendislik kısıtları' : 'Quick engineering constraints'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'cnc', en: 'CNC Milling', tr: 'CNC Freze', value: 'Manufacturing preference: CNC milling' },
                    { id: 'al6061', en: 'Al 6061-T6', tr: 'Al 6061-T6', value: 'Material preference: Al 6061-T6' },
                    { id: 'iso2768', en: 'ISO 2768-m', tr: 'ISO 2768-m', value: 'Tolerance target: ISO 2768-m; verify on engineering drawing' },
                    { id: 'ip67', en: 'IP67', tr: 'IP67', value: 'Environmental target: IP67; qualification testing required' },
                  ].map((item) => {
                    const selected = briefMustInclude.split('\n').includes(item.value);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        aria-pressed={selected}
                        data-testid={`quick-constraint-${item.id}`}
                        onClick={() => {
                          setShowDetailedBrief(true);
                          setBriefMustInclude((previous) => {
                            const lines = previous.split('\n').map((line) => line.trim()).filter(Boolean);
                            return (lines.includes(item.value)
                              ? lines.filter((line) => line !== item.value)
                              : [...lines, item.value]).join('\n');
                          });
                        }}
                        className={`px-2.5 py-1.5 border text-[11px] font-mono transition-colors ${selected
                          ? 'border-cyan-300/60 bg-cyan-300/10 text-cyan-200'
                          : 'border-white/20 text-white/70 hover:border-cyan-300/40 hover:text-white'}`}
                      >
                        {language === 'tr' ? item.tr : item.en}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-white/50">
                  {language === 'tr'
                    ? 'Seçimler teknik brief’e eklenir; üretilebilirlik ve uygunluk ayrıca doğrulanmalıdır.'
                    : 'Selections join the technical brief; manufacturability and compliance still need validation.'}
                </p>
              </div>
            </div>

            {/* Detailed Brief — optional structured constraints panel */}
            <div className="mb-3">
              <button
                type="button"
                onClick={() => setShowDetailedBrief(!showDetailedBrief)}
                data-testid="toggle-detailed-brief"
                className="w-full flex items-center justify-between px-3 py-2 border border-white/10 hover:border-white/25 hover:bg-white/5 transition-colors text-left"
              >
                <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/60">
                  <Sparkles className="w-3 h-3 text-cyan-300" />
                  {language === 'tr' ? 'Detaylı Brief (Opsiyonel)' : 'Detailed Brief (Optional)'}
                </span>
                <span className="text-[10px] text-white/40 font-mono">
                  {showDetailedBrief ? '▲' : '▼'}
                </span>
              </button>
              <AnimatePresence>
                {showDetailedBrief && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-3 mt-3 p-3 border border-white/5 bg-black/20">
                      <p className="text-[10px] text-white/40 leading-relaxed">
                        {language === 'tr'
                          ? 'Karmaşık projeler için yapılandırılmış kısıtlar belirleyin — AI bunları aynen uygular.'
                          : 'Pin precise constraints for complex projects — AI will honor them exactly.'}
                      </p>

                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-cyan-300/80 block mb-1">
                          {language === 'tr' ? 'Mutlaka olmalı' : 'Must include'}
                        </label>
                        <textarea
                          value={briefMustInclude}
                          onChange={(e) => setBriefMustInclude(e.target.value)}
                          rows={3}
                          placeholder={language === 'tr'
                            ? '4 koltuk\n4 batarya (lityum-iyon)\nGüneş paneli (üst yüzey)\nGizli pervane bölmesi\nGizli tekerlek bölmesi\nSesli komut sistemi\nTam otonom sürüş'
                            : '4 seats\n4 lithium-ion batteries\nSolar panels (top surface)\nRetractable propellers\nRetractable wheels\nVoice command system\nFull autonomous driving'}
                          data-testid="brief-must-include"
                          className="w-full bg-[#060710] border border-white/10 focus:border-cyan-300/40 text-white text-xs px-3 py-2 font-mono resize-none outline-none transition-colors"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-rose-300/80 block mb-1">
                          {language === 'tr' ? 'Olmamalı' : 'Must exclude'}
                        </label>
                        <textarea
                          value={briefMustExclude}
                          onChange={(e) => setBriefMustExclude(e.target.value)}
                          rows={2}
                          placeholder={language === 'tr' ? 'Benzin motoru\nManuel sürüş' : 'Combustion engine\nManual driving'}
                          data-testid="brief-must-exclude"
                          className="w-full bg-[#060710] border border-white/10 focus:border-rose-300/40 text-white text-xs px-3 py-2 font-mono resize-none outline-none transition-colors"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] font-mono uppercase tracking-wider text-white/50 block mb-1">
                            {language === 'tr' ? 'Hedef kullanıcı' : 'Target users'}
                          </label>
                          <input
                            type="text"
                            value={briefTargetUsers}
                            onChange={(e) => setBriefTargetUsers(e.target.value)}
                            placeholder={language === 'tr' ? 'Şehir içi aile, 4 kişi' : 'Urban family of 4'}
                            data-testid="brief-target-users"
                            className="w-full bg-[#060710] border border-white/10 focus:border-white/30 text-white text-xs px-3 py-2 font-mono outline-none transition-colors"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-mono uppercase tracking-wider text-white/50 block mb-1">
                            {language === 'tr' ? 'Çalışma ortamı' : 'Operating env.'}
                          </label>
                          <input
                            type="text"
                            value={briefOperatingEnv}
                            onChange={(e) => setBriefOperatingEnv(e.target.value)}
                            placeholder={language === 'tr' ? 'Şehir + 500m irtifa' : 'Urban + 500m altitude'}
                            data-testid="brief-operating-env"
                            className="w-full bg-[#060710] border border-white/10 focus:border-white/30 text-white text-xs px-3 py-2 font-mono outline-none transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-amber-300/80 block mb-1">
                          {language === 'tr' ? 'Güç / batarya / enerji notu' : 'Power / battery / energy note'}
                        </label>
                        <textarea
                          value={briefPowerNotes}
                          onChange={(e) => setBriefPowerNotes(e.target.value)}
                          rows={2}
                          placeholder={language === 'tr'
                            ? '4x lityum-iyon batarya, üst yüzeyde güneş paneli (200W), şarj 2 saat'
                            : '4x Li-ion batteries, 200W roof-mounted solar panel, 2-hour charge'}
                          data-testid="brief-power-notes"
                          className="w-full bg-[#060710] border border-white/10 focus:border-amber-300/40 text-white text-xs px-3 py-2 font-mono resize-none outline-none transition-colors"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-white/50 block mb-1">
                          {language === 'tr' ? 'Görseller (4 farklı boyut · zorunlu)' : 'Visual views (4 angles · required)'}
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {[
                            { id: 'front',     label: language === 'tr' ? 'Ön' : 'Front', required: true },
                            { id: 'side',      label: language === 'tr' ? 'Yan' : 'Side', required: true },
                            { id: 'top',       label: language === 'tr' ? 'Üst' : 'Top', required: true },
                            { id: 'iso',       label: language === 'tr' ? 'İzometrik' : 'Iso', required: true },
                            { id: 'interior',  label: language === 'tr' ? 'İç Tasarım' : 'Interior', required: false },
                            { id: 'cutaway',   label: language === 'tr' ? 'Kesit' : 'Cutaway', required: false },
                            { id: 'dashboard', label: language === 'tr' ? 'Gösterge Paneli' : 'Dashboard', required: false },
                            { id: 'rear',      label: language === 'tr' ? 'Arka' : 'Rear', required: false },
                            { id: 'exploded',  label: language === 'tr' ? 'Patlamış' : 'Exploded', required: false },
                          ].map((v) => {
                            const active = briefViews.includes(v.id);
                            const locked = v.required;
                            return (
                              <button
                                type="button"
                                key={v.id}
                                onClick={() => {
                                  if (locked) return; // can't deselect required views
                                  setBriefViews((prev) => active ? prev.filter(x => x !== v.id) : [...prev, v.id]);
                                }}
                                data-testid={`brief-view-${v.id}`}
                                title={locked
                                  ? (language === 'tr' ? 'Bu görünüm zorunlu — ISO 7200 standardı + Sustainability hesabı için gerekli' : 'Required view — needed for ISO 7200 standard + sustainability calc')
                                  : ''}
                                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider transition-colors border ${
                                  active
                                    ? (locked
                                        ? 'border-emerald-300/50 bg-emerald-300/10 text-emerald-200 cursor-default'
                                        : 'border-cyan-300/60 bg-cyan-300/10 text-cyan-200')
                                    : 'border-white/10 text-white/40 hover:border-white/25'
                                }`}
                              >
                                {active && '✓ '}{v.label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[10px] text-white/30 mt-1.5">
                          {language === 'tr'
                            ? `${briefViews.length} görünüm · 4 zorunlu (engineering drawing PDF'i gerektiriyor) · ~${briefViews.length * 10} sn`
                            : `${briefViews.length} views · 4 required for engineering drawing PDF · ~${briefViews.length * 10}s`}
                        </p>
                      </div>

                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-white/50 block mb-1">
                          {language === 'tr' ? 'Ek notlar' : 'Extra notes'}
                        </label>
                        <textarea
                          value={briefExtraNotes}
                          onChange={(e) => setBriefExtraNotes(e.target.value)}
                          rows={2}
                          placeholder={language === 'tr'
                            ? 'IP55 su geçirmez, sertifika ihtiyacı, üretim ülkesi, vs.'
                            : 'IP55 waterproof, certification needs, country of manufacture, etc.'}
                          data-testid="brief-extra-notes"
                          className="w-full bg-[#060710] border border-white/10 focus:border-white/30 text-white text-xs px-3 py-2 font-mono resize-none outline-none transition-colors"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Generate Button — opens Intent Review modal first (prompt-fidelity guard) */}
            <Button
              onClick={openIntentReview}
              disabled={!chatMessage.trim() || isGeneratingProProject || intentLoading}
              className="w-full bg-white text-black hover:bg-white/90 py-3 text-sm font-medium tracking-wide rounded-none disabled:opacity-40"
              data-testid="pro-generate-btn"
            >
              {isGeneratingProProject ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.generating}
                  {proProjectProgress > 0 && (
                    <span className="ml-2 font-mono text-xs opacity-70">{proProjectProgress}%</span>
                  )}
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  {t.generateProject}
                </>
              )}
            </Button>

            {/* Competitive Edge — "design something better than X and Y" */}
            <button
              onClick={openEdgeEngine}
              disabled={!chatMessage.trim() || isGeneratingProProject}
              className="group mt-2 w-full inline-flex items-center justify-center gap-2 border border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-emerald-500/10 hover:from-amber-500/20 hover:to-emerald-500/20 py-3 text-sm font-medium tracking-wide rounded-none text-amber-200 hover:text-amber-100 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="pro-edge-btn"
            >
              <svg className="w-4 h-4 text-amber-300 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 11H5a2 2 0 00-2 2v7a2 2 0 002 2h4M9 11V5a2 2 0 012-2h2a2 2 0 012 2v6M9 11h6m0 0v6a2 2 0 002 2h2a2 2 0 002-2v-6h-4z" />
              </svg>
              <span>
                {language === 'tr'
                  ? 'Rakipleri Yen · Competitive Edge'
                  : 'Beat Competitors · Competitive Edge'}
              </span>
            </button>

            {/* Live progress bar + stage label (visible during generation) */}
            {isGeneratingProProject && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3"
                data-testid="pro-progress-panel"
              >
                <div className="h-1.5 bg-white/5 overflow-hidden">
                  <motion.div
                    animate={{ width: `${Math.max(2, proProjectProgress || 2)}%` }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-cyan-400 via-violet-400 to-fuchsia-400"
                  />
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/50">
                  <span data-testid="pro-progress-stage">
                    {(() => {
                      const stageMap = {
                        starting: language === 'tr' ? 'Başlatılıyor…' : 'Starting…',
                        queued: language === 'tr' ? 'Kuyrukta' : 'Queued',
                        analysis_started: language === 'tr' ? 'Analiz başladı' : 'Analysis started',
                        analysis_llm: language === 'tr' ? 'AI proje üretiyor (gpt-4o · intent kilitli)' : 'AI generating project (gpt-4o · intent locked)',
                        parsing_response: language === 'tr' ? 'Yanıt ayrıştırılıyor' : 'Parsing response',
                        analysis_complete: language === 'tr' ? 'Analiz tamamlandı' : 'Analysis complete',
                        code_generation: language === 'tr' ? 'Başlangıç kodu üretiliyor' : 'Generating starter code',
                        code_complete: language === 'tr' ? 'Kod hazır' : 'Code ready',
                        finalizing: language === 'tr' ? 'Sonuçlar finalize ediliyor' : 'Finalizing',
                        complete: language === 'tr' ? 'Tamamlandı' : 'Complete',
                        error: language === 'tr' ? 'Hata' : 'Error',
                      };
                      return stageMap[proProjectStage] || proProjectStage || (language === 'tr' ? 'Çalışıyor…' : 'Working…');
                    })()}
                  </span>
                  <span>{proProjectProgress}%</span>
                </div>
                <p className="mt-2 text-[10px] text-white/30 leading-relaxed">
                  {language === 'tr'
                    ? 'Üretim 20-90 saniye arası sürer · İstediğiniz zaman bu sekmeden ayrılabilirsiniz, iş arkada devam eder.'
                    : 'Generation takes 20-90 seconds · You can navigate away — the job continues in the background.'}
                </p>

                {/* Skeleton preview — shows the eventual layout fading in for better perceived perf */}
                <div className="mt-6">
                  <ProProjectSkeleton language={language} />
                </div>
              </motion.div>
            )}

            {/* Inline Error (replaces alert()) */}
            {proProjectError && (() => {
              const rawMsg = String(proProjectError.message || '');
              const isBudget = /budget has been exceeded|budget exceeded|max budget|insufficient (credit|balance|funds)/i.test(rawMsg);
              return (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-4 border p-4 ${isBudget ? 'border-amber-500/40 bg-amber-500/5' : 'border-red-500/30 bg-red-500/5'}`}
                data-testid="pro-project-error"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 border flex items-center justify-center flex-shrink-0 ${isBudget ? 'border-amber-500/40' : 'border-red-500/40'}`}>
                    <X className={`w-4 h-4 ${isBudget ? 'text-amber-400' : 'text-red-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${isBudget ? 'text-amber-400/80' : 'text-red-400/80'}`}>
                      {isBudget
                        ? (language === 'tr' ? 'AI Bütçesi Tükendi' : 'AI Budget Exhausted')
                        : proProjectError.code === 429
                        ? (language === 'tr' ? 'Demo Limiti' : 'Demo Limit')
                        : proProjectError.code === 401
                        ? (language === 'tr' ? 'Giriş Gerekli' : 'Login Required')
                        : (language === 'tr' ? 'Hata' : 'Error')}
                    </div>
                    <p className="text-sm text-white/80 leading-relaxed" data-testid="pro-project-error-msg">
                      {isBudget
                        ? (language === 'tr'
                          ? 'AI üretim hizmeti şu anda kullanılamıyor. Birkaç dakika sonra yeniden deneyin; sorun sürerse destek ekibine bildirin.'
                          : 'The AI generation service is temporarily unavailable. Try again in a few minutes; contact support if the issue persists.')
                        : rawMsg}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {isBudget && (
                        <a
                          href="/contact"
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-black text-xs font-medium hover:bg-amber-400 transition-colors"
                          data-testid="pro-error-topup-btn"
                        >
                          {language === 'tr' ? 'Destek Al' : 'Contact Support'} →
                        </a>
                      )}
                      {proProjectError.requiresLogin && !isBudget && (
                        <a
                          href="/login"
                          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-black text-xs font-medium hover:bg-white/90 transition-colors"
                          data-testid="pro-error-login-btn"
                        >
                          {language === 'tr' ? 'Giriş Yap — Sınırsız Erişim' : 'Login — Unlimited Access'}
                        </a>
                      )}
                      <button
                        onClick={() => setProProjectError(null)}
                        className="inline-flex items-center px-4 py-2 border border-white/15 text-white/70 text-xs hover:bg-white/5 hover:text-white transition-colors"
                      >
                        {language === 'tr' ? 'Kapat' : 'Dismiss'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
              );
            })()}

            {/* Pro Project Results */}
            {proProject && showProProjectResult && (
              <motion.div
                ref={proResultRef}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 border-t border-slate-700 pt-6"
              >
                {/* Fidelity Verification Card — "Your request → Generated design" trust signal */}
                {lastConfirmedIntent && (() => {
                  const summary = proProject.project?.project_summary || {};
                  const title = summary.title || '';
                  const lockedSummary = lastConfirmedIntent.inferred_summary || lastOriginalPrompt;
                  const lockedCategory = lastConfirmedIntent.product_category || '';
                  // Heuristic match check: do major keywords from the locked intent appear in the title/description?
                  const haystack = JSON.stringify(proProject.project || {}).toLowerCase();
                  const lockedKeywords = (lockedCategory + ' ' + (lastConfirmedIntent.must_have_features || []).join(' '))
                    .toLowerCase()
                    .split(/\s+/)
                    .filter(w => w.length > 3);
                  const matches = lockedKeywords.filter(k => haystack.includes(k)).length;
                  const matchPct = lockedKeywords.length > 0
                    ? Math.round((matches / lockedKeywords.length) * 100)
                    : 95;
                  const matchOk = matchPct >= 60;
                  return (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mb-5 rounded-xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/5 via-transparent to-emerald-500/5 p-4"
                      data-testid="fidelity-card"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-cyan-300" />
                          <span className="text-xs font-semibold uppercase tracking-widest text-cyan-300">
                            {language === 'tr' ? 'Tutarlılık Kontrolü' : 'Consistency Check'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${matchOk ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                            {matchOk ? <CheckCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                            {matchPct}% {language === 'tr' ? 'EŞLEŞME' : 'MATCH'}
                          </div>
                        </div>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                          <div className="mb-1 text-[10px] font-mono uppercase tracking-wider text-white/40">
                            {language === 'tr' ? 'Onayladığınız' : 'You confirmed'}
                          </div>
                          <p className="text-sm font-medium text-white/90 leading-snug">"{lockedSummary}"</p>
                          {lockedCategory && (
                            <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-cyan-500/10 px-2 py-0.5 text-[10px] text-cyan-300 border border-cyan-500/30">
                              {lockedCategory}
                            </div>
                          )}
                        </div>
                        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                          <div className="mb-1 text-[10px] font-mono uppercase tracking-wider text-emerald-300/80">
                            {language === 'tr' ? 'Üretilen Tasarım' : 'Generated design'}
                          </div>
                          <p className="text-sm font-medium text-white leading-snug">{title}</p>
                          {summary.subtitle && (
                            <p className="mt-1 text-[11px] text-white/50 leading-snug">{summary.subtitle}</p>
                          )}
                        </div>
                      </div>
                      {!matchOk && (
                        <p className="mt-3 text-[11px] text-amber-300/80 leading-relaxed">
                          {language === 'tr'
                            ? 'Düşük eşleşme tespit edildi. Tasarım onayladığınız özelliklerin bir kısmından sapmış olabilir — sonuçları kontrol edin veya yeniden üretin.'
                            : 'Low match detected. The generated design may have drifted from your confirmed intent — review the result or regenerate.'}
                        </p>
                      )}
                    </motion.div>
                  );
                })()}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    {t.projectReady}
                  </h3>
                  <div className="flex gap-2">
                    <Button onClick={downloadProjectJSON} variant="outline" size="sm" className="border-slate-600">
                      <Download className="w-4 h-4 mr-1" />
                      {t.downloadJson}
                    </Button>
                  </div>
                </div>

                {proProject.validation && (
                  <div
                    className={`mb-4 border p-4 ${
                      proProject.validation.completion_eligible
                        ? 'border-emerald-500/30 bg-emerald-950/10'
                        : 'border-red-500/40 bg-red-950/15'
                    }`}
                    data-testid="pro-validation-panel"
                  >
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest mb-2">
                      <Shield className="w-3.5 h-3.5" />
                      <span className={proProject.validation.completion_eligible ? 'text-emerald-300' : 'text-red-300'}>
                        {proProject.validation.completion_eligible
                          ? (language === 'tr' ? 'Deterministik kontroller geçti' : 'Deterministic checks passed')
                          : (language === 'tr' ? 'Mühendislik kapıları tamamlanmadı' : 'Engineering gates are incomplete')}
                      </span>
                    </div>
                    <p className="text-xs text-white/70 mb-2">
                      {language === 'tr'
                        ? 'BOM, kütle, maliyet ve çıktı tutarlılığı sunucu tarafından yeniden hesaplandı.'
                        : 'BOM, mass, cost and output consistency were recalculated by the server.'}
                    </p>
                    {[...(proProject.validation.blocking_issues || []), ...(proProject.validation.warnings || [])].length > 0 && (
                      <ul className="space-y-1 text-[11px] text-white/75">
                        {[...(proProject.validation.blocking_issues || []), ...(proProject.validation.warnings || [])]
                          .slice(0, 8)
                          .map((item, index) => (
                            <li key={index}>
                              • {typeof item === 'string'
                                ? item
                                : (item?.[language] || item?.en || item?.tr || item?.message || JSON.stringify(item))}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                )}

                {proProject.project?.quality_warnings?.length > 0 && (
                  <div
                    className="mb-4 border border-amber-500/40 bg-amber-950/20 p-4"
                    data-testid="pro-quality-warning"
                  >
                    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-amber-300 mb-2">
                      <Shield className="w-3.5 h-3.5" />
                      {language === 'tr' ? 'Kısmi çıktı · uzman incelemesi gerekli' : 'Partial output · expert review required'}
                      <span className="text-amber-300/60" data-testid="pro-quality-warning-count">
                        {warningSummary(proProject.project.quality_warnings, language)}
                      </span>
                    </div>
                    <p className="text-xs text-amber-100/80 leading-relaxed mb-2">
                      {language === 'tr'
                        ? 'Kalite kapısının bazı maddeleri tamamlanamadı. Bu paketi üretim veya saha kararı için kullanmayın.'
                        : 'Some quality-gate requirements remain incomplete. Do not use this package for production or field decisions.'}
                    </p>
                    <div className="space-y-3">
                      {groupWarnings(proProject.project.quality_warnings, language).map((group) => (
                        <div key={group.section}>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300/70 mb-1">
                            {group.label}
                          </div>
                          <ul className="space-y-1">
                            {group.items.map((item, index) => (
                              <li
                                key={`${group.section}-${index}`}
                                className={`text-[11px] leading-relaxed text-amber-100/80 ${
                                  item.translated ? '' : 'font-mono'
                                }`}
                              >
                                • {item.text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project Summary */}
                <DesignRequirements requirements={proProject.project?.design_requirements} language={language} />
                {proProject.project?.project_summary && (
                  <div className="bg-gradient-to-br from-amber-950/30 to-orange-950/30 rounded-xl p-5 mb-4 border border-amber-500/20">
                    <h4 className="text-lg font-bold text-white mb-1">
                      {proProject.project.project_summary.title}
                    </h4>
                    <p className="text-amber-300 text-sm mb-3">{proProject.project.project_summary.subtitle}</p>
                    <p className="text-slate-300 text-sm mb-4">{proProject.project.project_summary.description}</p>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 uppercase mb-1">{t.targetMarket}</p>
                        <p className="text-white text-sm">{proProject.project.project_summary.target_market?.join(', ')}</p>
                      </div>
                      <div className="bg-slate-900/50 rounded-lg p-3">
                        <p className="text-xs text-slate-500 uppercase mb-1">{t.problemSolved}</p>
                        <p className="text-white text-sm">{proProject.project.project_summary.problem_solved}</p>
                      </div>
                    </div>
                  </div>
                )}

<details className="mb-4 rounded-xl border border-white/10 p-4" data-testid="pro-engineering-details">
<summary className="cursor-pointer text-sm font-medium text-white focus-visible:outline focus-visible:outline-cyan-300">{language === 'tr' ? 'Teknik inceleme ve çıktı listesi' : 'Engineering review and deliverables'}</summary>
<p className="mt-2 mb-4 text-xs text-slate-400">{language === 'tr' ? 'Sistem mimarisi, güvenlik, doğrulama planı ve prototip aşamaları' : 'Architecture, safety, verification plan and prototype stages'}</p>
                {/* Safety-critical engineering package — assumptions, architecture and gates */}
                {(proProject.project?.system_architecture || proProject.project?.engineering_analysis || proProject.project?.safety_case ||
                  proProject.project?.verification_plan?.length || proProject.project?.prototype_roadmap?.length) && (
                  <div className="grid md:grid-cols-2 gap-3 mb-4" data-testid="engineering-package">
                    {proProject.project?.system_architecture && (
                      <div className="border border-cyan-500/20 bg-cyan-950/10 p-4">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-3">
                          {language === 'tr' ? 'Sistem Mimarisi' : 'System Architecture'}
                        </div>
                        {Object.entries(proProject.project.system_architecture).slice(0, 7).map(([key, value]) => (
                          <div key={key} className="mb-2 last:mb-0">
                            <div className="text-[10px] font-mono uppercase text-white/40">{key.replaceAll('_', ' ')}</div>
                            <div className="text-xs text-white/75 leading-relaxed"><StructuredValue value={value} /></div>
                          </div>
                        ))}
                      </div>
                    )}
                    {proProject.project?.engineering_analysis && (
                      <div className="border border-blue-500/20 bg-blue-950/10 p-4" data-testid="engineering-analysis">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-blue-300 mb-3">
                          {language === 'tr' ? 'Mühendislik Analizi' : 'Engineering Analysis'}
                        </div>
                        <div className="text-xs text-white/75 leading-relaxed">
                          <StructuredValue value={proProject.project.engineering_analysis} />
                        </div>
                      </div>
                    )}
                    {proProject.project?.safety_case && (
                      <div className="border border-amber-500/30 bg-amber-950/10 p-4">
                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-amber-300 mb-3">
                          <Shield className="w-3.5 h-3.5" />
                          {language === 'tr' ? 'Güvenlik Dosyası · Uzman Onayı Gerekli' : 'Safety Case · Expert Review Required'}
                        </div>
                        <p className="text-xs text-white/80 leading-relaxed mb-3">
                          {proProject.project.safety_case.safety_threshold}
                        </p>
                        {(proProject.project.safety_case.prohibited_actions || []).map((item, index) => (
                          <div key={index} className="flex gap-2 text-xs text-amber-100/80 mb-1">
                            <span>•</span><span>{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {proProject.project?.verification_plan?.length > 0 && (
                      <div className="border border-white/10 bg-white/[0.02] p-4">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-3">
                          {language === 'tr' ? 'Doğrulama Planı' : 'Verification Plan'}
                        </div>
                        {proProject.project.verification_plan.slice(0, 5).map((item, index) => (
                          <div key={index} className="mb-2 last:mb-0 text-xs text-white/75">
                            <span className="font-semibold text-white">{item.phase || `${index + 1}`}: </span>
                            {item.test} — {item.acceptance_criteria}
                          </div>
                        ))}
                      </div>
                    )}
                    {proProject.project?.prototype_roadmap?.length > 0 && (
                      <div className="border border-white/10 bg-white/[0.02] p-4">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-violet-300 mb-3">
                          {language === 'tr' ? 'Prototip Yol Haritası' : 'Prototype Roadmap'}
                        </div>
                        {proProject.project.prototype_roadmap.slice(0, 5).map((item, index) => (
                          <div key={index} className="mb-2 last:mb-0 text-xs text-white/75">
                            <span className="font-semibold text-white">{item.phase}. {item.name}</span>
                            <span className="text-white/45"> · {item.duration_weeks}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Deliverable existence is never presented as engineering verification. */}
                <div className="mb-4 border border-white/10 bg-[#060710] p-4" data-testid="pro-output-guide">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/70 mb-3">
                    {language === 'tr' ? 'Prototip Çıktıları Kontrol Listesi' : 'Prototype Deliverables Checklist'}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {[
                      {
                        label: language === 'tr' ? '1. Gereksinimler ve mimari' : '1. Requirements & architecture',
                        generated: !!proProject.project,
                      },
                      {
                        label: language === 'tr' ? '2. BOM ve malzemeler' : '2. BOM & materials',
                        generated: (proProject.project?.components?.length || 0) >= 12,
                      },
                      {
                        label: language === 'tr' ? '3. Altı teknik 2B görünüm' : '3. Six technical 2D views',
                        generated: Boolean(
                          proProject.concept_views?.orthographic
                          || ['front', 'rear', 'left', 'right', 'top', 'bottom'].every((view) => proProject.concept_views?.[view])
                        ),
                        working: proProject.artifact_status?.visuals?.status === 'generating',
                      },
                      {
                        label: language === 'tr' ? '4. İç kesit ve patlatılmış görünüm' : '4. Cutaway & exploded view',
                        generated: !!(proProject.concept_views?.cutaway && proProject.concept_views?.exploded),
                        working: proProject.artifact_status?.visuals?.status === 'generating',
                      },
                      {
                        label: language === 'tr' ? '5. Döndürülebilir 3B model' : '5. Rotatable 3D model',
                        generated: mesh3dStatus === 'completed' && !!mesh3dModelUrl,
                        working: ['starting','running'].includes(mesh3dStatus),
                      },
                      {
                        label: language === 'tr' ? '6. Kod paketi' : '6. Code package',
                        generated: !includeCode || (proProject.code?.files?.length || 0) > 0,
                      },
                      {
                        label: language === 'tr' ? '7. Sunum paketi' : '7. Presentation package',
                        generated: (proProject.project?.presentation_sections?.length || 0) >= 5,
                      },
                    ].map((step) => {
                      const state = step.working ? 'working' : step.generated ? 'review' : 'missing';
                      const stateLabel = state === 'working'
                        ? (language === 'tr' ? 'Üretiliyor' : 'Generating')
                        : state === 'review'
                          ? (language === 'tr' ? 'Üretildi · İnceleme Gerekli' : 'Generated · Review Required')
                          : 'TBD';
                      return (
                      <div key={step.label} className="flex items-center gap-2 border border-white/5 bg-white/[0.02] px-3 py-2">
                        {state === 'working'
                          ? <Loader2 className="w-4 h-4 shrink-0 text-cyan-300 animate-spin" />
                          : <AlertTriangle className="w-4 h-4 shrink-0 text-amber-300" />}
                        <span className={`text-xs flex-1 ${state === 'missing' ? 'text-white/55' : 'text-white/85'}`}>{step.label}</span>
                        <span className={`text-[9px] font-mono uppercase text-right ${state === 'working' ? 'text-cyan-300' : 'text-amber-300'}`}>
                          {stateLabel}
                        </span>
                      </div>
                    )})}
                  </div>
                  <p className="mt-3 text-[10px] font-mono text-amber-200/70" data-testid="pro-output-trust-note">
                    {language === 'tr'
                      ? '“Üretildi” yalnızca dosya varlığını doğrular. Ölçüler, etiketler, geometri, BOM, kod ve finansal değerler nitelikli insan incelemesine kadar ön çalışmadır; CAD doğrulaması veya test sonucu değildir.'
                      : '“Generated” confirms file existence only. Dimensions, labels, geometry, BOM, code, and financial values remain preliminary until qualified human review; this is not CAD verification or a test result.'}
                  </p>
                </div>

</details>

                {/* Multi-View Concept Gallery */}
                {proProject?.project_id && (
                  <div className="mb-4" data-testid="pro-multi-view-gallery">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">
                        {language === 'tr' ? 'Konsept Görselleri' : 'Concept Visuals'}
                      </div>
                      <div className="text-[10px] font-mono text-white/40" data-testid="gallery-view-count">
                        {Object.keys(usableConceptViews(proProject.concept_views)).length || (resolveAssetUrl(proProject.concept_image) ? 1 : 0)} / {
                          new Set([
                            ...Object.keys(proProject.concept_views || {}),
                            ...(briefViews?.length ? briefViews : ['orthographic', 'exploded', 'cutaway']),
                          ]).size
                        }
                      </div>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                      {[...new Set([
                        ...Object.keys(proProject.concept_views || {}),
                        ...(briefViews?.length ? briefViews : ['orthographic', 'exploded', 'cutaway']),
                      ])].map((view) => {
                        const viewLabels = {
                          orthographic: language === 'tr' ? 'Altı Görünüş Ortografik Sayfa' : 'Six-View Orthographic Sheet',
                          front: language === 'tr' ? 'Önden' : 'Front',
                          side: language === 'tr' ? 'Yandan' : 'Side',
                          top: language === 'tr' ? 'Üstten' : 'Top',
                          iso: language === 'tr' ? 'İzometrik' : 'Isometric',
                          rear: language === 'tr' ? 'Arkadan' : 'Rear',
                          exploded: language === 'tr' ? 'Patlatılmış' : 'Exploded',
                          cutaway: language === 'tr' ? 'İç Kesit' : 'Cutaway',
                        };
                        const url = resolveAssetUrl(
                          (proProject.concept_views && proProject.concept_views[view]) || null
                        );
                        const visualStatus = proProject.artifact_status?.visuals?.status || (isGeneratingProProject ? 'generating' : 'tbd');
                        const isPending = visualStatus === 'generating';
                        return (
                          <div
                            key={view}
                            data-testid={`view-tile-${view}`}
                            className={`relative aspect-square border bg-[#060710] overflow-hidden ${
                              url ? 'border-cyan-300/30' : 'border-white/10'
                            }`}
                          >
                            {url ? (
                              <a href={url} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                                <img src={url} alt={viewLabels[view] || view} className="w-full h-full object-contain" />
                              </a>
                            ) : (
                              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3">
                                {isPending
                                  ? <Loader2 className="w-5 h-5 text-cyan-300/60 animate-spin" />
                                  : <AlertTriangle className="w-5 h-5 text-amber-300/80" />}
                                <span className={`text-[10px] font-mono text-center ${isPending ? 'text-white/40' : 'text-amber-200/70'}`}>
                                  {isPending
                                    ? (language === 'tr' ? 'Üretiliyor…' : 'Generating…')
                                    : (language === 'tr' ? 'Üretilemedi · TBD' : 'Not generated · TBD')}
                                </span>
                              </div>
                            )}
                            {url && (
                              <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-amber-950/90 border border-amber-400/30">
                                <span className="text-[8px] font-mono uppercase tracking-wider text-amber-200">
                                  {language === 'tr' ? 'İnceleme Gerekli' : 'Review Required'}
                                </span>
                              </div>
                            )}
                            <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/70 backdrop-blur-sm">
                              <span className="text-[9px] font-mono uppercase tracking-wider text-white/80">
                                {viewLabels[view] || view}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Interactive Annotated BOM Drawing — appears once at least one view + BOM components are loaded */}
                {((proProject.concept_views && Object.keys(proProject.concept_views).length > 0) || proProject.concept_image) &&
                  (proProject.project?.components?.length > 0) && (
                    <AnnotatedDrawingViewer
                      project={{
                        ...proProject,
                        project_data: proProject.project,
                        sustainability: proProject.sustainability,
                      }}
                      isTr={language === 'tr'}
                    />
                )}

                {/* Image-to-3D Panel — shown immediately; starts automatically when views arrive. */}
                {proProject?.project_id && (
                  <div className="mb-4 border border-cyan-300/20 bg-gradient-to-br from-cyan-950/20 via-[#060710] to-violet-950/20 p-4" data-testid="pro-3d-mesh-panel">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Box className="w-4 h-4 text-cyan-300" />
                        <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300">
                          {m3('title')}
                        </div>
                        <span className="text-[9px] font-mono text-white/40 hidden sm:inline">{language === 'tr' ? 'Ön 3D görsel ağ' : 'Preliminary 3D visual mesh'}</span>
                      </div>
                      {mesh3dStatus === 'completed' && mesh3dModelUrl && (
                        <span className="text-[10px] font-mono text-emerald-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {m3('ready')}
                        </span>
                      )}
                    </div>

                    {/* Idle / failed → show CTA */}
                    {(mesh3dStatus === 'idle' || mesh3dStatus === 'failed') && (
                      <div className="flex flex-col gap-3">
                        <p className="text-xs text-white/60 leading-relaxed">
                          {m3('desc')}
                        </p>
                        <Button
                          onClick={generate3DMesh}
                          disabled={!((proProject?.concept_views && Object.keys(proProject.concept_views).length > 0) || proProject?.concept_image)}
                          title={!((proProject?.concept_views && Object.keys(proProject.concept_views).length > 0) || proProject?.concept_image)
                            ? (language === 'tr' ? 'Önce en az bir konsept görseli üretin' : 'Generate at least one concept visual first')
                            : undefined}
                          data-testid="generate-3d-btn"
                          className="bg-white text-black hover:bg-white/90 rounded-none font-mono uppercase tracking-wider text-xs"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          {m3('cta')}
                        </Button>
                        {mesh3dStatus === 'failed' && mesh3dError && (
                          <div className="text-xs text-red-300 font-mono border border-red-500/30 bg-red-950/30 p-2" data-testid="mesh-3d-error">
                            {m3('errorPrefix')}{mesh3dError}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Running */}
                    {(mesh3dStatus === 'starting' || mesh3dStatus === 'running') && (
                      <div className="space-y-2" data-testid="mesh-3d-progress">
                        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider">
                          <span className="text-cyan-300 flex items-center gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            {(() => {
                              const stageMap = {
                                queued: m3('queued'),
                                uploading_images: m3('uploading'),
                                generating_mesh: m3('generating'),
                                finalizing: m3('finalizing'),
                              };
                              return stageMap[mesh3dStage] || m3('working');
                            })()}
                          </span>
                          <span className="text-white/60">{mesh3dProgress}%</span>
                        </div>
                        <div className="h-1 bg-white/5 overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-cyan-400 to-violet-400"
                            animate={{ width: `${Math.max(2, mesh3dProgress || 2)}%` }}
                            transition={{ duration: 0.4 }}
                          />
                        </div>
                        <p className="text-[10px] font-mono text-white/40">
                          {m3('duration')}
                        </p>
                      </div>
                    )}

                    {/* Completed → embedded model-viewer + download links */}
                    {mesh3dStatus === 'completed' && mesh3dModelUrl && (
                      <div className="space-y-3" data-testid="mesh-3d-result">
                        <div className="aspect-video w-full border border-white/10 bg-[#040409] overflow-hidden">
                          {/* @google/model-viewer is loaded globally via index.html script tag */}
                          <model-viewer
                            src={`${API_URL}/api/proxy/model?url=${encodeURIComponent(mesh3dModelUrl)}`}
                            alt="Generated 3D model"
                            camera-controls
                            auto-rotate
                            ar
                            ar-modes="webxr scene-viewer quick-look"
                            ar-scale="auto"
                            ar-placement="floor"
                            shadow-intensity="1"
                            exposure="0.9"
                            style={{ width: '100%', height: '100%', backgroundColor: '#040409' }}
                          >
                            <button
                              slot="ar-button"
                              data-testid="studio-ar-launch-btn"
                              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-black text-[11px] font-mono uppercase tracking-wider hover:bg-white/90"
                              type="button"
                            >
                              {language === 'tr' ? 'AR\'da Görüntüle' : 'View in AR'}
                            </button>
                          </model-viewer>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={downloadProGlb}
                            disabled={!!isExportingCad}
                            data-testid="download-glb-btn"
                            className="inline-flex items-center gap-2 px-3 py-2 bg-white text-black hover:bg-white/90 font-mono uppercase tracking-wider text-xs"
                          >
                            <Download className="w-4 h-4" />
                            {m3('download')}
                            {mesh3dFileSize && (
                              <span className="opacity-60 normal-case">({formatBytes(mesh3dFileSize)})</span>
                            )}
                          </button>
                          <a
                            href={mesh3dModelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-white/80 hover:bg-white/5 font-mono uppercase tracking-wider text-xs"
                          >
                            <ExternalLink className="w-4 h-4" />
                            {m3('openTab')}
                          </a>
                          <span className="text-[10px] font-mono text-white/40 ml-auto">
                            {m3('compat')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Verified Pro project exports — CAD interoperability, not a fake native CAD claim. */}
                {proProject.project_id && (
                  <div className="mb-4 border border-white/10 bg-[#060710] p-4" data-testid="pro-cad-export-panel">
                    <div className="flex flex-col gap-1 mb-3">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300">
                        {language === 'tr' ? 'CAD ve Üretim Çıktıları' : 'CAD & Manufacturing Outputs'}
                      </div>
                      <p className="text-xs text-white/55">
                        {language === 'tr'
                          ? 'AutoCAD için DXF; görsel 3B mesh için STL; CAD araçlarında referans yüzeyler için STEP/IGES. STEP/IGES çıktıları düzenlenebilir katı parça değil, yaklaşık yüzey kabuğudur ve mühendis doğrulaması gerekir.'
                          : 'DXF for AutoCAD; STL for a visual 3D mesh; STEP/IGES for reference surfaces in CAD tools. STEP/IGES outputs are approximate faceted shells, not editable solid parts, and require engineering validation.'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => exportCadFile('dxf')}
                        disabled={!!isExportingCad}
                        data-testid="pro-export-dxf-btn"
                        className="h-8 rounded-none border-white/20 text-xs"
                      >
                        {isExportingCad === 'dxf' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                        DXF
                      </Button>
                      {['stl', 'step', 'iges'].map((format) => (
                        <Button
                          key={format}
                          size="sm"
                          variant="outline"
                          onClick={() => exportCadFile(format, mesh3dModelUrl)}
                          disabled={!!isExportingCad || !mesh3dModelUrl}
                          data-testid={`pro-export-${format}-btn`}
                          className="h-8 rounded-none border-white/20 text-xs"
                          title={!mesh3dModelUrl
                            ? (language === 'tr' ? 'Önce 3D prototipi tamamlayın' : 'Complete the 3D prototype first')
                            : undefined}
                        >
                          {isExportingCad === format ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                          {format.toUpperCase()}
                        </Button>
                      ))}
                      <Button
                        size="sm"
                        onClick={createOnshapeCAD}
                        disabled={isCreatingCAD || !mesh3dModelUrl}
                        data-testid="pro-onshape-btn"
                        className="h-8 rounded-none bg-white text-black hover:bg-white/90 text-xs"
                        title={!mesh3dModelUrl
                          ? (language === 'tr' ? 'Önce doğrulanabilir 3B modeli tamamlayın' : 'Complete a verifiable 3D model first')
                          : undefined}
                      >
                        {isCreatingCAD ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Package className="w-3 h-3 mr-1" />}
                        Onshape
                      </Button>
                    </div>
                  </div>
                )}


                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {['summary', 'specs', 'materials', 'costs', 'manufacturing', 'market'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                        activeTab === tab
                          ? 'bg-amber-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {t[tab === 'specs' ? 'techSpecs' : tab]}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="bg-slate-900/50 rounded-xl p-4 max-h-96 overflow-y-auto">
                  {activeTab === 'specs' && proProject.project?.technical_specifications && (
                    <div className="space-y-3">
                      <h5 className="text-white font-medium">{t.techSpecs}</h5>
                      <div className="text-xs text-slate-300 bg-slate-950 p-3 rounded-lg">
                        <StructuredValue value={proProject.project.technical_specifications} />
                      </div>
                    </div>
                  )}

                  {activeTab === 'materials' && proProject.project?.materials && (
                    <div className="space-y-2">
                      <h5 className="text-white font-medium mb-3">{t.materials}</h5>
                      <div className="space-y-2">
                        {proProject.project.materials.map((mat, i) => {
                          const cost = mat.unit_cost ?? mat.cost ?? mat.price;
                          const fmt = (v) => v !== null && v !== undefined && Number.isFinite(Number(v))
                            ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                            : '—';
                          return (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-950/50 rounded-lg">
                              <div className="min-w-0">
                                <p className="text-white text-sm font-medium truncate">{mat.name || `Material ${i + 1}`}</p>
                                <p className="text-slate-400 text-xs truncate">{[mat.type, mat.quantity].filter(Boolean).join(' • ')}</p>
                              </div>
                              <span className="text-amber-400 font-medium ml-3 flex-shrink-0">{fmt(cost)}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="pt-4 mt-4 border-t border-white/10">
                        <h6 className="text-[10px] font-mono uppercase tracking-wider text-amber-300 mb-2">
                          {language === 'tr' ? `BOM Bileşenleri (${proProject.project?.components?.length || 0})` : `BOM Components (${proProject.project?.components?.length || 0})`}
                        </h6>
                        <div className="space-y-2">
                          {(proProject.project?.components || []).map((component, i) => (
                            <div key={i} className="p-2 bg-slate-950/50 rounded-lg">
                              <div className="flex items-start justify-between gap-3">
                                <p className="text-white text-sm font-medium">{i + 1}. {component.name || `Component ${i + 1}`}</p>
                                <span className="text-[10px] text-amber-300 whitespace-nowrap">×{component.quantity ?? 1}</span>
                              </div>
                              <p className="text-slate-400 text-xs mt-1">
                                {[component.type, component.primary_material, component.weight_grams != null ? `${component.weight_grams} g` : null].filter(Boolean).join(' • ')}
                              </p>
                              {component.specifications ? <div className="text-[11px] text-slate-500 mt-1"><StructuredValue value={component.specifications} /></div> : null}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'costs' && proProject.project?.cost_analysis && (() => {
                    const ca = proProject.project.cost_analysis || {};
                    // Map either schema (backend prompt vs legacy frontend) into a unified view.
                    const materials = Number(ca.materials_cost ?? 0);
                    const labor = Number(ca.labor_cost ?? 0);
                    const tooling = Number(ca.tooling_cost ?? 0);
                    const overhead = Number(ca.overhead_cost ?? 0);
                    const recurringUnitCost = Number(ca.unit_cost_excluding_tooling ?? ca.total_unit_cost ?? (materials + labor + overhead));
                    // Named for what it measures: one unit built while paying the
                    // one-time tooling bill once. Deliberately NOT ca.total_cost,
                    // which is a per-unit figure (provenance labels it "Total unit
                    // cost") and reads as a 12,067 unit once NRE is folded into it.
                    const firstBuildTotal = recurringUnitCost + tooling;
                    const retail = Number(ca.suggested_retail_price ?? ca.suggested_retail ?? 0);
                    const costLines = proProject.project?.materials || [];
                    const hasTbdCost = costLines.some((line) => {
                      const value = line?.unit_cost ?? line?.cost ?? line?.price;
                      return value == null || value === '' || !Number.isFinite(Number(value));
                    });
                    // Reconciliation and currency rules live in src/lib/costPresentation.js
                    // so they can be unit-tested without rendering this component.
                    const qualityWarnings = proProject.project?.quality_warnings;
                    const costReconciled = isCostReconciled({
                      costAnalysis: ca,
                      hasTbdCost,
                      costDiscrepancies: proProject.validation?.cost_discrepancies,
                      qualityWarnings,
                    });
                    const margin = costReconciled && retail > 0 && recurringUnitCost > 0
                      // Recompute from the reconciled recurring unit cost;
                      // never trust an LLM margin that included one-time NRE.
                      ? Math.round(((retail - recurringUnitCost) / retail) * 100)
                      : null;
                    // Unit economics without a unit is not a number anyone can read, so
                    // every figure carries the currency and a missing one is stated openly
                    // rather than rendered as a bare number.
                    const currency = costCurrency(ca);
                    const fmt = (v) => formatCostValue(v, currency);
                    return (
                      <div className="space-y-3">
                        <h5 className="text-white font-medium">{t.costs}</h5>
                        <div className={`rounded border px-3 py-2 text-[10px] font-mono uppercase tracking-wider ${costReconciled
                          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                          : 'border-amber-500/30 bg-amber-500/10 text-amber-200'}`}>
                          {costReconciled
                            ? (language === 'tr' ? 'BOM ile uzlaştırıldı' : 'Reconciled to BOM')
                            : (language === 'tr' ? 'Ön tahmin · uzlaştırılmadı' : 'Preliminary · unreconciled')}
                        </div>
                        {!currency && (
                          <div
                            className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-amber-200"
                            data-testid="cost-currency-missing"
                          >
                            {language === 'tr'
                              ? 'Para birimi belirtilmedi · rakamlar birimsiz'
                              : 'Currency not stated · figures are unitless'}
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-slate-950/50 rounded-lg p-3">
                            <p className="text-xs text-slate-500">{language === 'tr' ? 'Malzeme Maliyeti' : 'Materials Cost'}</p>
                            <p className="text-lg font-bold text-white">{fmt(materials)}</p>
                          </div>
                          <div className="bg-slate-950/50 rounded-lg p-3">
                            <p className="text-xs text-slate-500">{language === 'tr' ? 'İşçilik Maliyeti' : 'Labor Cost'}</p>
                            <p className="text-lg font-bold text-white">{fmt(labor)}</p>
                          </div>
                          <div className="bg-slate-950/50 rounded-lg p-3">
                            <p className="text-xs text-slate-500">{language === 'tr' ? 'Kalıp/Takım · Tek Seferlik NRE' : 'Tooling · One-time NRE'}</p>
                            <p className="text-lg font-bold text-white">{fmt(tooling)}</p>
                          </div>
                          <div className="bg-slate-950/50 rounded-lg p-3">
                            <p className="text-xs text-slate-500">{language === 'tr' ? 'Genel Gider' : 'Overhead'}</p>
                            <p className="text-lg font-bold text-white">{fmt(overhead)}</p>
                          </div>
                          <div className="bg-amber-950/50 rounded-lg p-3 border border-amber-500/30">
                            <p className="text-xs text-amber-400">
                              {language === 'tr' ? 'Tekrarlayan Birim Maliyeti' : 'Recurring Unit Cost'}{costReconciled ? '' : (language === 'tr' ? ' · Ön Tahmin' : ' · Preliminary')}
                            </p>
                            <p className="text-xl font-bold text-amber-400">{fmt(recurringUnitCost)}</p>
                          </div>
                          <div className={`rounded-lg p-3 border ${costReconciled
                            ? 'bg-green-950/50 border-green-500/30'
                            : 'bg-slate-950/50 border-slate-600'}`}>
                            <p className={`text-xs ${costReconciled ? 'text-green-400' : 'text-slate-500'}`}>{t.retailPrice}</p>
                            <p className={`text-xl font-bold ${costReconciled ? 'text-green-400' : 'text-slate-300'}`}>
                              {costReconciled ? fmt(retail) : 'TBD'}
                            </p>
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          {language === 'tr' ? 'NRE dahil ilk paket toplamı' : 'Initial package total including NRE'}: {fmt(firstBuildTotal)}
                        </p>
                        <div className="mt-3 p-3 bg-blue-950/50 rounded-lg border border-blue-500/30">
                          <p className="text-xs text-blue-400">{t.profitMargin}</p>
                          <p className="text-2xl font-bold text-blue-400">{margin == null || !Number.isFinite(margin) ? 'TBD' : `${margin}%`}</p>
                        </div>
                      </div>
                    );
                  })()}

                  {activeTab === 'manufacturing' && proProject.project?.manufacturing_plan && (() => {
                    const mp = proProject.project.manufacturing_plan || {};
                    const method = mp.production_method || mp.process || '—';
                    const steps = mp.production_steps || mp.assembly_instructions || [];
                    const timeline = mp.timeline_weeks;
                    const qc = mp.quality_control || [];
                    const tests = mp.testing_procedures || [];
                    return (
                      <div className="space-y-3">
                        <h5 className="text-white font-medium mb-3">{t.manufacturing}</h5>
                        <p className="text-slate-300 text-sm mb-3">{method}</p>
                        {timeline ? (
                          <p className="text-amber-400 text-xs mb-3">
                            {language === 'tr' ? 'Süre' : 'Timeline'}: {timeline} {language === 'tr' ? 'hafta' : 'weeks'}
                          </p>
                        ) : null}
                        <div className="space-y-2">
                          {steps.map((step, i) => {
                            const isObj = typeof step === 'object' && step !== null;
                            const stepNum = isObj ? (step.step || i + 1) : (i + 1);
                            const name = isObj ? (step.name || step.title || `Step ${stepNum}`) : String(step);
                            const desc = isObj ? (step.description || step.detail || '') : '';
                            const duration = isObj ? (step.duration || '') : '';
                            return (
                              <div key={i} className="flex gap-3 p-2 bg-slate-950/50 rounded-lg">
                                <span className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{stepNum}</span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm font-medium">{name}</p>
                                  {desc ? <p className="text-slate-400 text-xs">{desc}</p> : null}
                                  {duration ? <p className="text-amber-400 text-xs mt-1">{duration}</p> : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {qc.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider font-mono">
                              {language === 'tr' ? 'Kalite Kontrol' : 'Quality Control'}
                            </p>
                            <ul className="space-y-1">
                              {qc.map((q, i) => (
                                <li key={i} className="text-sm text-slate-300 pl-3 border-l border-emerald-500/40">{typeof q === 'string' ? q : (q.check || q.description || JSON.stringify(q))}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {tests.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider font-mono">
                              {language === 'tr' ? 'Test Prosedürleri' : 'Testing Procedures'}
                            </p>
                            <ul className="space-y-1">
                              {tests.map((tt, i) => (
                                <li key={i} className="text-sm text-slate-300 pl-3 border-l border-blue-500/40">{typeof tt === 'string' ? tt : (tt.test || tt.description || JSON.stringify(tt))}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {activeTab === 'market' && proProject.project?.market_analysis && (
                    <div className="space-y-3">
                      <h5 className="text-white font-medium mb-3">{t.market}</h5>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="bg-slate-950/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 mb-2">{language === 'tr' ? 'Hedef Sektörler' : 'Target Industries'}</p>
                          <div className="flex flex-wrap gap-1">
                            {proProject.project.market_analysis.target_industries?.map((ind, i) => (
                              <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">{ind}</span>
                            ))}
                          </div>
                        </div>
                        <div className="bg-slate-950/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 mb-2">{language === 'tr' ? 'Rekabet Avantajları' : 'Competitive Advantages'}</p>
                          <ul className="space-y-1">
                            {proProject.project.market_analysis.competitive_advantages?.map((adv, i) => (
                              <li key={i} className="text-xs text-green-300 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />{adv}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="text-xs text-slate-300 bg-slate-950/50 rounded-lg p-3">
                        <StructuredValue value={Object.fromEntries(Object.entries(proProject.project.market_analysis).filter(([key]) => !['target_industries', 'competitive_advantages'].includes(key)))} />
                      </div>
                    </div>
                  )}

                  {activeTab === 'summary' && (
                    <div className="space-y-3">
                      <h5 className="text-white font-medium mb-3">{t.summary}</h5>
                      <div className="text-xs text-slate-300 bg-slate-950 p-3 rounded-lg">
                        <StructuredValue value={proProject.project?.project_summary} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Code Output */}
                {proProject.code && (
                  <details className="mt-4 bg-slate-900/50 rounded-xl p-4">
                    <summary className="cursor-pointer text-white font-medium flex items-center gap-2">
                      <Code className="w-4 h-4 text-green-400" />
                      {language === 'tr' ? 'Üretilen Kod' : 'Generated Code'}
                    </summary>
                    <div className="space-y-2">
                      {proProject.code.files?.map((file, i) => (
                        <div key={i} className="bg-slate-950 rounded-lg p-3">
                          <p className="text-amber-400 text-sm font-mono mb-2">{file.filename}</p>
                          <button
                            type="button"
                            disabled={typeof file.content !== 'string' || !file.content.trim()}
                            onClick={() => triggerBlobDownload(
                              new Blob([file.content], { type: 'text/plain;charset=utf-8' }),
                              (file.filename || 'source.txt').replace(/[\\/]/g, '_'),
                            )}
                            className="text-xs text-cyan-300 underline disabled:opacity-40"
                          >
                            {language === 'tr' ? 'Tam kaynak dosyasını indir' : 'Download full source file'}
                          </button>
                          <pre className="text-xs text-slate-300 overflow-x-auto max-h-40">
                            {file.content?.substring(0, 500)}...
                          </pre>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {includeCode && !(proProject.code?.files?.length > 0) && (
                  <div className="mt-4 border border-amber-500/30 bg-amber-500/10 rounded-xl p-4 text-amber-200 text-sm" data-testid="pro-code-missing-warning">
                    {language === 'tr'
                      ? 'Kod paketi oluşturulamadı; bu çıktı tamamlanmış olarak işaretlenmedi. Yeniden üretim veya mühendislik incelemesi gerekli.'
                      : 'The code package was not generated and is not marked complete. Regeneration or engineering review is required.'}
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        ) : (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="grid lg:grid-cols-5 gap-5">

          {/* Concept Image Viewer */}
          <div className="lg:col-span-3 glass rounded-2xl overflow-hidden flex flex-col border border-blue-500/20" style={{ minHeight: '650px' }}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-white/10 bg-gradient-to-r from-slate-900 to-slate-800">
              <div className="flex items-center gap-3">
                <ImageIcon className="w-5 h-5 text-violet-400" />
                <span className="font-medium text-white text-sm">{t.viewer}</span>
                {conceptImage && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-green-400 bg-green-500/20">
                    <CheckCircle className="w-3.5 h-3.5" />
                    {language === 'tr' ? 'Hazır' : 'Ready'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={clearAll} aria-label={language === 'tr' ? 'Sohbeti temizle' : 'Clear chat'} className="text-slate-400 hover:text-white h-8 w-8 p-0">
                  <RotateCw className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={handleExport} disabled={!design} aria-label={language === 'tr' ? 'Tasarımı dışa aktar' : 'Export design'} className="text-slate-400 hover:text-white h-8 w-8 p-0">
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Design Name */}
            {design?.design_specs?.name && (
              <div className="px-4 py-2 bg-[#0A0A0A] border-b border-white/5">
                <h3 className="text-white font-medium text-lg">{design.design_specs.name}</h3>
                {design.design_specs.dimensions && (
                  <span className="text-xs text-slate-400">
                    {design.design_specs.dimensions.width} x {design.design_specs.dimensions.height} x {design.design_specs.dimensions.depth} {design.design_specs.dimensions.unit}
                  </span>
                )}
              </div>
            )}

            {/* Concept Image Display */}
            <div className="flex-1 relative bg-[#060710] flex items-center justify-center" style={{ minHeight: '400px' }}>
              {conceptImage ? (
                <div className="p-6 text-center">
                  <div className="relative inline-block">
                    <img
                      src={conceptImage}
                      alt="AI Generated Concept"
                      className="rounded-xl shadow-2xl max-h-[400px] border-2 border-violet-500/30"
                      data-testid="concept-image"
                    />
                    <div className="absolute -top-2 -right-2 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full p-2">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <p className="text-violet-400 font-medium mt-4">
                    {language === 'tr' ? '✨ AI Konsept Görseli' : '✨ AI Concept Image'}
                  </p>
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <a
                      href={conceptImage}
                      download="nexus-concept.png"
                      className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
                    >
                      <Download className="w-4 h-4" />
                      {language === 'tr' ? 'İndir' : 'Download'}
                    </a>
                    <Button
                      onClick={() => setShowShareModal(true)}
                      className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
                      data-testid="share-design-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      {language === 'tr' ? 'Paylaş' : 'Share'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  {isGenerating ? (
                    <div className="text-center">
                      <div className="relative w-20 h-20 mx-auto mb-4">
                        <div className="absolute inset-0 rounded-full border-4 border-violet-500/20 animate-ping" />
                        <div className="absolute inset-2 rounded-full border-4 border-t-violet-400 border-r-purple-400 border-b-transparent border-l-transparent animate-spin" />
                      </div>
                      <p className="text-violet-400 font-medium">{language === 'tr' ? 'AI Konsept Görsel Oluşturuluyor...' : 'AI Generating Concept Image...'}</p>
                      <p className="text-slate-500 text-sm mt-1">{language === 'tr' ? 'Bu işlem 30-60 saniye sürebilir' : 'This may take 30-60 seconds'}</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <ImageIcon className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                      <p className="text-slate-400">{t.aiReady}</p>
                      <p className="text-slate-400 text-sm mt-2">
                        {language === 'tr' ? 'Örnek: "Robot kollu akıllı duş kabini"' : 'Example: "Smart shower cabin with robotic arms"'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Simulation & Animation Buttons */}
            {design && (
              <div className="p-3 border-t border-white/10 bg-slate-900/50">
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <span className="text-xs text-slate-400 mr-2">{t.simulation}:</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runSimulation('stress')}
                    disabled={isSimulating}
                    className="h-7 text-xs border-slate-700 hover:bg-slate-800"
                  >
                    <Scale3D className="w-3 h-3 mr-1" />
                    {t.stress}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runSimulation('thermal')}
                    disabled={isSimulating}
                    className="h-7 text-xs border-slate-700 hover:bg-slate-800"
                  >
                    <ThermometerSun className="w-3 h-3 mr-1" />
                    {t.thermal}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runSimulation('weight')}
                    disabled={isSimulating}
                    className="h-7 text-xs border-slate-700 hover:bg-slate-800"
                  >
                    <Cpu className="w-3 h-3 mr-1" />
                    {t.weight}
                  </Button>

                  {/* Schematic assembly-sequence animation button */}
                  <Button
                    size="sm"
                    onClick={generateExplodedView}
                    disabled={isGeneratingExploded}
                    className="h-7 text-xs bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white border-0"
                    data-testid="exploded-view-btn"
                  >
                    {isGeneratingExploded ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Layers className="w-3 h-3 mr-1" />
                    )}
                    {language === 'tr' ? 'Montaj Animasyonu' : 'Assembly Animation'}
                  </Button>

                  {/* Presentation Button */}
                  <Button
                    size="sm"
                    onClick={generatePresentation}
                    disabled={isGeneratingPresentation}
                    className="h-7 text-xs bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 text-white border-0"
                    data-testid="generate-presentation-btn"
                  >
                    {isGeneratingPresentation ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Presentation className="w-3 h-3 mr-1" />
                    )}
                    {language === 'tr' ? 'Sunum Oluştur' : 'Create Presentation'}
                  </Button>

                  {/* 3D Prototype Button */}
                  <Button
                    size="sm"
                    onClick={generate3DPrototype}
                    disabled={isGenerating3D || meshyStatus === 'PENDING' || meshyStatus === 'IN_PROGRESS'}
                    className="h-7 text-xs bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white border-0"
                    data-testid="generate-3d-btn"
                    title={language === 'tr'
                      ? 'Meshy AI ile 3D görselleştirme modeli oluşturur. Mühendislik CAD değil, görsel prototip içindir.'
                      : 'Creates 3D visualization model with Meshy AI. For visual prototyping, not engineering CAD.'}
                  >
                    {(isGenerating3D || meshyStatus === 'PENDING' || meshyStatus === 'IN_PROGRESS') ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        {meshyProgress > 0 ? `${meshyProgress}%` : ''}
                      </>
                    ) : (
                      <Box className="w-3 h-3 mr-1" />
                    )}
                    {language === 'tr' ? '3D Prototip' : '3D Prototype'}
                  </Button>

                  {/* Onshape CAD Button */}
                  <Button
                    size="sm"
                    onClick={createOnshapeCAD}
                    disabled={isCreatingCAD}
                    className="h-7 text-xs bg-white text-black hover:bg-white/90 rounded-none border-0"
                    data-testid="create-cad-btn"
                    title={language === 'tr'
                      ? 'Onshape\'te profesyonel CAD dokümanı oluşturur. 3D modeli varsa, içe aktarır.'
                      : 'Creates professional CAD document in Onshape. Imports 3D model if available.'}
                  >
                    {isCreatingCAD ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Package className="w-3 h-3 mr-1" />
                    )}
                    {language === 'tr' ? 'Onshape' : 'Onshape'}
                  </Button>

                  {/* Co-Pilot Mode — export to industry-standard CAD formats */}
                  {conceptImage && (
                    <div className="relative">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCopilotMenuOpen((v) => !v)}
                        className="h-7 text-xs border-white/20 text-white/80 hover:bg-white/5 hover:border-white/30 rounded-none"
                        data-testid="copilot-export-btn"
                        aria-expanded={copilotMenuOpen}
                        aria-haspopup="menu"
                        title={language === 'tr'
                          ? 'Co-Pilot Modu — Tasarımı AutoCAD, SolidWorks veya Fusion 360 için export edin.'
                          : 'Co-Pilot Mode — Export the design for AutoCAD, SolidWorks or Fusion 360.'}
                      >
                        <Download className="w-3 h-3 mr-1" />
                        {language === 'tr' ? 'Co-Pilot Export' : 'Co-Pilot Export'}
                      </Button>
                      {/* Dropdown menu */}
                      {copilotMenuOpen && (
                        <>
                          {/* click-outside catcher */}
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setCopilotMenuOpen(false)}
                            aria-hidden="true"
                          />
                          <div
                            role="menu"
                            className="absolute right-0 top-full mt-1 w-56 bg-[#0A0A0A] border border-white/10 shadow-2xl z-20"
                            data-testid="copilot-export-menu"
                          >
                        <div className="px-3 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-white/40">
                          {language === 'tr' ? 'Format Seç' : 'Choose Format'}
                        </div>
                        {!rawGlbUrl && (
                          <div className="px-3 py-2 border-b border-white/10 text-[10px] text-amber-300/80 leading-relaxed">
                            {language === 'tr'
                              ? '3D formatlar (STL/OBJ/GLB/STEP/IGES) için önce 3D prototip oluşturun — şu an yalnızca DXF aktif.'
                              : 'Generate a 3D prototype first for 3D formats (STL/OBJ/GLB/STEP/IGES) — only DXF is available now.'}
                          </div>
                        )}
                        {[
                          { fmt: 'stl', label: 'STL (.stl)', hint: '3D printing · SolidWorks import', needsModel: true },
                          { fmt: 'obj', label: 'OBJ (.obj)', hint: 'Rendering · visualization', needsModel: true },
                          { fmt: 'glb', label: 'GLB (.glb)', hint: 'Web · AR/VR', needsModel: true },
                          { fmt: '3mf', label: '3MF (.3mf)', hint: 'Modern 3D printing', needsModel: true },
                          { fmt: 'ply', label: 'PLY (.ply)', hint: 'Scanning · point cloud', needsModel: true },
                          { fmt: 'dxf', label: 'DXF (.dxf)', hint: '2D floor plan · AutoCAD', needsModel: false },
                          { fmt: 'step', label: 'STEP (.step)', hint: 'ISO 10303 · faceted CAD shell', needsModel: true },
                          { fmt: 'iges', label: 'IGES (.iges)', hint: 'Legacy CAD exchange', needsModel: true },
                        ].map((opt) => (
                          <button
                            key={opt.fmt}
                            disabled={isExportingCad || (opt.needsModel && !rawGlbUrl)}
                            onClick={() => {
                              setCopilotMenuOpen(false);
                              exportCadFile(opt.fmt, rawGlbUrl);
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors flex flex-col gap-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`copilot-format-${opt.fmt}`}
                          >
                            <span className="text-xs text-white font-medium flex items-center gap-1.5">
                              {isExportingCad === opt.fmt && <Loader2 className="w-3 h-3 animate-spin" />}
                              {opt.label}
                            </span>
                            <span className="text-[10px] text-white/40 font-mono">{opt.hint}</span>
                          </button>
                        ))}
                        <div className="px-3 py-2 border-t border-white/10 text-[10px] text-white/40 leading-relaxed">
                          {language === 'tr'
                            ? 'STEP/IGES dosyaları faceted shell. Editlenebilir parametrik B-Rep için Onshape\'i kullanın.'
                            : 'STEP/IGES files are faceted shells. For editable parametric B-Rep, use Onshape.'}
                        </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Open in Onshape Button (if CAD exists) */}
                  {cadDocument && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(cadDocument.onshape_url, '_blank')}
                      className="h-7 text-xs border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                      data-testid="open-onshape-btn"
                    >
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Onshape
                    </Button>
                  )}

                  {/* View Simulation Results Button */}
                  {simulationResult && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowSimulationModal(true)}
                      className="h-7 text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                    >
                      <Activity className="w-3 h-3 mr-1" />
                      {language === 'tr' ? 'Simülasyon' : 'Simulation'}
                    </Button>
                  )}

                  {/* View Exploded View Button */}
                  {explodedViewData && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowExplodedModal(true)}
                      className="h-7 text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                    >
                      <Layers className="w-3 h-3 mr-1" />
                      {language === 'tr' ? 'Montaj Görünümü' : 'Assembly View'}
                    </Button>
                  )}

                  {/* View Presentation Button (if exists) */}
                  {presentation && (
                    <Button
                      size="sm"
                      onClick={() => setShowPresentation(true)}
                      className="h-7 text-xs bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white border-0"
                    >
                      <FileText className="w-3 h-3 mr-1" />
                      {language === 'tr' ? 'Sunumu Gör' : 'View Presentation'}
                    </Button>
                  )}

                  {/* View 3D Model Button (if ready) */}
                  {model3DUrl && model3DUrl.length > 0 && (
                    <Button
                      size="sm"
                      onClick={() => setShow3DViewer(true)}
                      className="h-7 text-xs bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white border-0"
                      data-testid="view-3d-btn"
                    >
                      <Box className="w-3 h-3 mr-1" />
                      {language === 'tr' ? '3D Görüntüle' : 'View 3D'}
                    </Button>
                  )}

                  {/* 3D Generation Progress */}
                  {(meshyStatus === 'PENDING' || meshyStatus === 'IN_PROGRESS') && (
                    <div className="flex items-center gap-2 px-2 py-1 bg-cyan-500/20 rounded-full">
                      <Loader2 className="w-3 h-3 text-cyan-400 animate-spin" />
                      <span className="text-xs text-cyan-400">
                        {language === 'tr' ? `3D: ${meshyProgress}%` : `3D: ${meshyProgress}%`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Chat & Info Panel */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {/* Chat Area */}
            <div className="bg-[#0A0A0A] border border-white/10 flex flex-col min-h-[520px]">
              <div className="px-4 py-3 border-b border-white/10 bg-[#0A0A0A]">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-white/70" />
                  <span className="text-sm font-medium text-white">
                    {language === 'tr' ? 'AI Prototip Asistanı' : 'AI Prototype Assistant'}
                  </span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {chatHistory.length === 0 && (
                  <div className="py-4" data-testid="prototype-copilot-onboarding">
                    <div className="text-white text-sm font-medium mb-2">
                      {language === 'tr' ? 'Ne üretmek istediğinizi birlikte netleştirelim.' : 'Let’s turn your idea into a complete brief.'}
                    </div>
                    <p className="text-slate-400 text-xs leading-relaxed mb-3">
                      {language === 'tr'
                        ? 'Tek mesajda bildiklerinizi yazın. Eksik kalan kritik bilgileri asistan sorar; Pro modunda bütün çıktılar adım adım izlenir.'
                        : 'Share what you know in one message. The copilot asks only for critical gaps; Pro mode tracks every deliverable step by step.'}
                    </p>
                    <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono text-white/55 mb-3">
                      {(language === 'tr'
                        ? ['1 · Amaç ve kullanıcı', '2 · Ortam ve boyut', '3 · Güç ve hareket', '4 · Zorunlu özellikler', '5 · Yasaklar / güvenlik', '6 · Bütçe ve hedef']
                        : ['1 · Goal and user', '2 · Environment and size', '3 · Power and motion', '4 · Must-have features', '5 · Exclusions / safety', '6 · Budget and target']
                      ).map((item) => <div key={item} className="border border-white/5 bg-white/[0.02] px-2 py-1.5">{item}</div>)}
                    </div>
                    <button
                      type="button"
                      onClick={() => setChatMessage(language === 'tr'
                        ? 'Profesyonel kullanıcılar için [ürün] tasarla. Kullanım ortamı: […]. Yaklaşık boyut: […]. Güç: […]. Zorunlu özellikler: […]. Olmaması gerekenler: […]. Pro pakette 2B görünümler, iç kesit, patlatılmış görünüm, döndürülebilir 3B, kod, BOM ve yatırımcı sunumu üret.'
                        : 'Design a [product] for professional users. Environment: […]. Approximate size: […]. Power: […]. Must-have features: […]. Exclusions: […]. In Pro, generate 2D views, cutaway, exploded view, rotatable 3D, code, BOM and investor presentation.')}
                      className="w-full border border-cyan-400/25 bg-cyan-400/5 px-3 py-2 text-left text-xs text-cyan-200 hover:bg-cyan-400/10 transition-colors"
                      data-testid="complete-brief-template"
                    >
                      {language === 'tr' ? 'Tam prototip şablonunu doldur →' : 'Fill the complete prototype brief →'}
                    </button>
                  </div>
                )}
                {chatHistory.map((msg, i) => (
                  <ChatBubble key={i} text={msg.text} isUser={msg.isUser} isSystem={msg.isSystem} />
                ))}
                <div ref={chatEndRef} />
              </div>

              {/* Design Counter */}
              <div className="px-3 py-2 border-t border-white/5 flex items-center justify-between">
                <span className="text-xs text-slate-400">
                  {isAdminUser() ? (
                    <span className="text-emerald-400">
                      {language === 'tr' ? '∞ Sınırsız (Admin)' : '∞ Unlimited (Admin)'}
                    </span>
                  ) : (
                    <>
                      {language === 'tr'
                        ? `${designCount}/${designLimit === 9999 ? '∞' : designLimit} tasarım kullanıldı`
                        : `${designCount}/${designLimit === 9999 ? '∞' : designLimit} designs used`}
                      {isDemo && <span className="ml-1 text-amber-400">(Demo)</span>}
                    </>
                  )}
                </span>
                {!isDemo && !isAdminUser() && userPlan === 'free' && designCount >= designLimit - 1 && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
                  >
                    <Crown className="w-3 h-3" />
                    {language === 'tr' ? 'Yükselt' : 'Upgrade'}
                  </button>
                )}
              </div>

              <div className="p-3 border-t border-white/10">
                <div className="mb-2 border border-cyan-400/20 bg-cyan-400/[0.04] p-2.5" data-testid="always-smart-copilot">
                  <div className="flex items-start gap-2">
                    <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                    {/* min-w-0 lets this flex child shrink below its content width,
                        so the copy below wraps instead of overflowing the panel */}
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-white">
                        {language === 'tr' ? 'AI Tasarım Sohbeti' : 'AI Design Copilot'}
                      </p>
                      <p className="mt-0.5 text-[11px] leading-relaxed text-white/50">
                        {language === 'tr'
                          ? 'Fikrinizi normal şekilde yazın. AI eksikleri anlar, yalnızca gerekli soruları sorar, cevaplarınızı hatırlar ve hazır olduğunda üretimi otomatik başlatır.'
                          : 'Describe your idea naturally. AI finds gaps, asks only what matters, remembers your answers, and starts generation automatically when ready.'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {/* Templates Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowTemplatesModal(true)}
                    className="shrink-0 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                    title={language === 'tr' ? 'Şablonlar' : 'Templates'}
                    aria-label={language === 'tr' ? 'Şablonlar' : 'Templates'}
                    data-testid="templates-btn"
                  >
                    <LayoutTemplate className="w-4 h-4" />
                  </Button>

                  {/* AI Voice Input Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={toggleVoice}
                    disabled={isTranscribing}
                    className={`shrink-0 ${
                      isRecording
                        ? 'text-red-400 animate-pulse bg-red-500/20'
                        : isTranscribing
                          ? 'text-amber-400'
                          : 'text-slate-400'
                    }`}
                    title={language === 'tr' ? 'AI Sesli Giriş' : 'AI Voice Input'}
                    aria-label={language === 'tr' ? 'AI Sesli Giriş' : 'AI Voice Input'}
                    data-testid="voice-input-btn"
                  >
                    {isTranscribing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isRecording ? (
                      <Volume2 className="w-4 h-4" />
                    ) : (
                      <Mic className="w-4 h-4" />
                    )}
                  </Button>

                  <textarea
                    rows={3}
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleChat();
                      }
                    }}
                    placeholder={t.placeholder}
                    aria-label={language === 'tr' ? 'Proje fikrinizi yazın' : 'Describe your project idea'}
                    className="flex-1 min-h-[72px] resize-y bg-transparent border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/30 transition-colors"
                    data-testid="chat-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleChat}
                    disabled={isGenerating || isChatting || !quotaReady || !chatMessage.trim()}
                    aria-label={language === 'tr' ? 'Mesajı gönder' : 'Send message'}
                    className="bg-white text-black hover:bg-white/90 rounded-none"
                    data-testid="send-btn"
                  >
                    {(isGenerating || isChatting) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* Specs Tabs */}
            {design && (
              <div className="glass rounded-2xl flex-1 overflow-hidden border border-blue-500/20">
                <div className="flex border-b border-white/10">
                  {(() => {
                    // Only show tabs that actually have content for Standard mode results.
                    // Prevents empty "Code / BOM / Features" placeholders (P0 fix).
                    const availableTabs = ['specs'];
                    if (design?.code && (design.code.language || (design.code.files && design.code.files.length))) availableTabs.push('code');
                    // BOM tab appears only if there are usable (non-placeholder) rows.
                    // Same filter as the tab body so we never show an empty pane.
                    const rawBom = design?.design_specs?.materials_breakdown
                      || design?.bill_of_materials?.items
                      || design?.bom;
                    const usableBom = Array.isArray(rawBom) ? rawBom.filter((it) => {
                      if (!it) return false;
                      if (typeof it !== 'object') return String(it).trim().length > 0;
                      const name = String(it.name || it.item || '').trim().toLowerCase();
                      return name && name !== 'unknown_material' && name !== 'unknown' && name !== 'n/a';
                    }) : [];
                    if (usableBom.length > 0) availableTabs.push('bom');
                    const featureList = design?.features || design?.design_specs?.features || design?.key_features;
                    if (Array.isArray(featureList) && featureList.length > 0) availableTabs.push('features');
                    return availableTabs;
                  })().map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`flex-1 px-3 py-2.5 text-xs font-medium transition-all ${
                        activeTab === tab
                          ? 'text-blue-400 bg-blue-500/10 border-b-2 border-blue-400'
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {t[tab]}
                    </button>
                  ))}
                </div>
                <div className="p-4 overflow-y-auto" style={{ maxHeight: '220px' }}>
                  {activeTab === 'specs' && design.design_specs && (
                    <div className="space-y-3 text-sm">
                      <div
                        className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs leading-relaxed text-amber-100"
                        data-testid="standard-evidence-notice"
                      >
                        {design.design_specs.validation_notice || (
                          language === 'tr'
                            ? 'Ön konsept: Boyut, ağırlık, malzeme, maliyet ve performans değerleri doğrulanmamış varsayımlardır; mühendislik ve tedarikçi doğrulaması gerekir.'
                            : 'Preliminary concept: dimensions, mass, materials, cost and performance are unverified assumptions requiring engineering and supplier validation.'
                        )}
                      </div>
                      {design.design_specs.dimensions && (
                        <div>
                          <span className="text-slate-400">{language === 'tr' ? 'Boyutlar:' : 'Dimensions:'}</span>
                          <span className="text-white ml-2">
                            {design.design_specs.dimensions.width} x {design.design_specs.dimensions.height} x {design.design_specs.dimensions.depth} {design.design_specs.dimensions.unit}
                          </span>
                        </div>
                      )}
                      {design.design_specs.weight && (
                        <div>
                          <span className="text-slate-400">{language === 'tr' ? 'Ağırlık:' : 'Weight:'}</span>
                          <span className="text-white ml-2">{typeof design.design_specs.weight === 'object' ? JSON.stringify(design.design_specs.weight) : design.design_specs.weight}</span>
                        </div>
                      )}
                      {design.design_specs.materials_breakdown && Array.isArray(design.design_specs.materials_breakdown) && (
                        <div>
                          <span className="text-slate-400">{language === 'tr' ? 'Malzemeler:' : 'Materials:'}</span>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {design.design_specs.materials_breakdown.map((m, i) => (
                              <span key={i} className="px-2 py-0.5 bg-slate-800 rounded text-xs text-slate-300">
                                {typeof m === 'object' ? (m.material || m.name || JSON.stringify(m)) : m}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {activeTab === 'code' && design.firmware_code && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Code className="w-4 h-4 text-green-400" />
                        <span className="text-green-400 text-xs font-medium">{design.firmware_code.filename || 'main.ino'}</span>
                      </div>
                      <pre className="bg-slate-900 rounded-lg p-3 text-xs text-slate-300 overflow-x-auto">
                        {typeof design.firmware_code.code === 'string' ? design.firmware_code.code.substring(0, 500) : 'Code not available'}...
                      </pre>
                    </div>
                  )}
                  {activeTab === 'bom' && design.bill_of_materials && (
                    <div className="space-y-2">
                      {Array.isArray(design.bill_of_materials.items) && design.bill_of_materials.items
                        .filter((item) => {
                          // Filter out placeholder rows the LLM sometimes emits
                          // (unknown_material, empty item name, $0 cost). P1 trust fix.
                          if (!item) return false;
                          if (typeof item !== 'object') return String(item).trim().length > 0;
                          const name = String(item.name || item.item || '').trim().toLowerCase();
                          if (!name || name === 'unknown_material' || name === 'unknown' || name === 'n/a') return false;
                          return true;
                        })
                        .slice(0, 6).map((item, i) => (
                        <div key={i} className="flex justify-between items-center p-2 bg-slate-800/50 rounded">
                          <span className="text-slate-300 text-xs">{typeof item === 'object' ? (item.name || item.item || 'Item') : item}</span>
                          <span className="text-green-400 text-xs font-medium">${typeof item === 'object' ? (item.estimated_cost || item.cost || item.price || '—') : '—'}</span>
                        </div>
                      ))}
                      {design.bill_of_materials.total_cost && Number(String(design.bill_of_materials.total_cost).replace(/[^0-9.]/g, '')) > 0 && (
                        <div className="flex justify-between items-center p-2 bg-green-500/10 rounded border border-green-500/20">
                          <span className="text-green-400 text-xs font-medium">{language === 'tr' ? 'Toplam' : 'Total'}</span>
                          <span className="text-green-400 text-sm font-bold">${typeof design.bill_of_materials.total_cost === 'object' ? JSON.stringify(design.bill_of_materials.total_cost) : design.bill_of_materials.total_cost}</span>
                        </div>
                      )}
                      <p className="text-[9px] italic text-white/40 mt-2">
                        {language === 'tr' ? 'Ön AI tahmini — üretim için gerçek tedarikçi teklifleri gereklidir.' : 'Preliminary AI estimate — real supplier quotes required for procurement.'}
                      </p>
                    </div>
                  )}
                  {activeTab === 'features' && design.design_specs?.features && Array.isArray(design.design_specs.features) && (
                    <ul className="space-y-2">
                      {design.design_specs.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <Box className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
                          <span className="text-slate-300">{typeof f === 'object' ? (f.name || f.feature || f.description || JSON.stringify(f)) : f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
        )}
      </div>

      {/* Simulation Results Modal */}
      <AnimatePresence>
        {showSimulationModal && simulationResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowSimulationModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-blue-500/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-[#0A0A0A]">
                <div className="flex items-center gap-3">
                  <Activity className="w-6 h-6 text-blue-400" />
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {language === 'tr' ? 'Mühendislik Simülasyonu' : 'Engineering Simulation'}
                    </h3>
                    <p className="text-sm text-slate-400 capitalize">{simulationResult.simulation_type || 'Analysis'}</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setShowSimulationModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
                <SimulationCard result={simulationResult} language={language} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Schematic assembly-sequence modal */}
      <AnimatePresence>
        {showExplodedModal && explodedViewData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowExplodedModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-purple-500/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-[#0A0A0A]">
                <div className="flex items-center gap-3">
                  <Layers className="w-6 h-6 text-purple-400" />
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {language === 'tr' ? 'Montaj Sırası Animasyonu' : 'Assembly Sequence Animation'}
                    </h3>
                    <p className="text-sm text-slate-400">
                      {explodedViewData.components?.length || 0} {language === 'tr' ? 'bileşen' : 'components'}
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => setShowExplodedModal(false)} className="text-slate-400 hover:text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
                <ExplodedViewAnimation
                  explodedData={explodedViewData}
                  isPlaying={isExplodedPlaying}
                  onToggle={() => setIsExplodedPlaying(!isExplodedPlaying)}
                  language={language}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Presentation Modal */}
      <AnimatePresence>
        {showPresentation && presentation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPresentation(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-[#0A0A0A]">
                <div className="flex items-center gap-3">
                  <Presentation className="w-6 h-6 text-orange-400" />
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {language === 'tr' ? 'Prototip Sunum Şeması' : 'Prototype Presentation'}
                    </h3>
                    <p className="text-sm text-slate-400">{presentation.product_overview?.name || currentPrompt}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={exportPresentationPdf}
                    disabled={isExportingPdf}
                    className="bg-red-600 hover:bg-red-500 text-white"
                    data-testid="export-pdf-btn"
                  >
                    {isExportingPdf ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-1" />
                    )}
                    PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={exportPresentation} className="border-slate-600">
                    <Download className="w-4 h-4 mr-1" />
                    JSON
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowPresentation(false)} className="text-slate-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {/* Product Overview */}
                  {presentation.product_overview && (
                    <PresentationCard title={language === 'tr' ? 'Ürün Tanımı' : 'Product Overview'} icon={Lightbulb} color="purple">
                      <div className="space-y-2 text-sm">
                        {presentation.product_overview.tagline && (
                          <p className="text-purple-300 font-medium italic">&ldquo;{presentation.product_overview.tagline}&rdquo;</p>
                        )}
                        <p className="text-slate-300">{presentation.product_overview.description}</p>
                        {presentation.product_overview.problem_solved && (
                          <div className="mt-2">
                            <span className="text-slate-400 text-xs">{language === 'tr' ? 'Çözülen Problem:' : 'Problem Solved:'}</span>
                            <p className="text-slate-300">{presentation.product_overview.problem_solved}</p>
                          </div>
                        )}
                        {presentation.product_overview.innovation_level && (
                          <span className="inline-block mt-2 px-2 py-1 bg-purple-500/20 rounded text-xs text-purple-300">
                            {presentation.product_overview.innovation_level}
                          </span>
                        )}
                      </div>
                    </PresentationCard>
                  )}

                  {/* Target Markets */}
                  {presentation.target_markets && Array.isArray(presentation.target_markets) && (
                    <PresentationCard title={language === 'tr' ? 'Hedef Pazarlar' : 'Target Markets'} icon={Target} color="blue">
                      <div className="space-y-3">
                        {presentation.target_markets.slice(0, 3).map((market, i) => (
                          <div key={i} className="p-2 bg-slate-800/50 rounded">
                            <div className="flex items-center justify-between">
                              <span className="text-white font-medium text-sm">{typeof market === 'object' ? market.sector : market}</span>
                              {market.market_size && <span className="text-blue-400 text-xs">{market.market_size}</span>}
                            </div>
                            {market.market_fit && <p className="text-slate-400 text-xs mt-1">{market.market_fit}</p>}
                          </div>
                        ))}
                      </div>
                    </PresentationCard>
                  )}

                  {/* Usage Scenarios */}
                  {presentation.usage_scenarios && Array.isArray(presentation.usage_scenarios) && (
                    <PresentationCard title={language === 'tr' ? 'Kullanım Senaryoları' : 'Usage Scenarios'} icon={Users} color="green">
                      <div className="space-y-3">
                        {presentation.usage_scenarios.slice(0, 2).map((scenario, i) => (
                          <div key={i} className="p-2 bg-slate-800/50 rounded">
                            <span className="text-white font-medium text-sm">{typeof scenario === 'object' ? scenario.scenario_name : scenario}</span>
                            {scenario.description && <p className="text-slate-400 text-xs mt-1">{scenario.description}</p>}
                            {scenario.steps && Array.isArray(scenario.steps) && (
                              <div className="mt-2 space-y-1">
                                {scenario.steps.slice(0, 3).map((step, j) => (
                                  <div key={j} className="flex items-center gap-1 text-xs text-slate-300">
                                    <ChevronRight className="w-3 h-3 text-green-400" />
                                    {typeof step === 'object' ? JSON.stringify(step) : step}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </PresentationCard>
                  )}

                  {/* Materials & Production */}
                  {presentation.materials_and_production && (
                    <PresentationCard title={language === 'tr' ? 'Malzemeler & Üretim' : 'Materials & Production'} icon={Factory} color="orange">
                      <div className="space-y-2">
                        {presentation.materials_and_production.primary_materials && Array.isArray(presentation.materials_and_production.primary_materials) && (
                          <div className="flex flex-wrap gap-1">
                            {presentation.materials_and_production.primary_materials.slice(0, 5).map((mat, i) => (
                              <span key={i} className="px-2 py-1 bg-orange-500/20 rounded text-xs text-orange-300">
                                {typeof mat === 'object' ? mat.material : mat}
                              </span>
                            ))}
                          </div>
                        )}
                        {presentation.materials_and_production.manufacturing_process && Array.isArray(presentation.materials_and_production.manufacturing_process) && (
                          <div className="mt-2">
                            <span className="text-slate-400 text-xs">{language === 'tr' ? 'Üretim Süreci:' : 'Process:'}</span>
                            <div className="mt-1 space-y-1">
                              {presentation.materials_and_production.manufacturing_process.slice(0, 3).map((step, i) => (
                                <div key={i} className="flex items-center gap-1 text-xs text-slate-300">
                                  <span className="w-4 h-4 flex items-center justify-center bg-orange-500/20 rounded-full text-orange-400">{i + 1}</span>
                                  {typeof step === 'object' ? JSON.stringify(step) : step}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </PresentationCard>
                  )}

                  {/* Timeline */}
                  {presentation.timeline && (
                    <PresentationCard title={language === 'tr' ? 'Zaman Çizelgesi' : 'Timeline'} icon={Clock} color="cyan">
                      <div className="space-y-2">
                        {presentation.timeline.development_phase && (
                          <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                            <span className="text-slate-300 text-xs">{language === 'tr' ? 'Geliştirme' : 'Development'}</span>
                            <span className="text-cyan-400 text-xs font-medium">{presentation.timeline.development_phase.duration}</span>
                          </div>
                        )}
                        {presentation.timeline.prototype_phase && (
                          <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                            <span className="text-slate-300 text-xs">{language === 'tr' ? 'Prototip' : 'Prototype'}</span>
                            <span className="text-cyan-400 text-xs font-medium">{presentation.timeline.prototype_phase.duration}</span>
                          </div>
                        )}
                        {presentation.timeline.production_phase && (
                          <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                            <span className="text-slate-300 text-xs">{language === 'tr' ? 'Üretim' : 'Production'}</span>
                            <span className="text-cyan-400 text-xs font-medium">{presentation.timeline.production_phase.duration}</span>
                          </div>
                        )}
                        {presentation.timeline.total_time_to_market && (
                          <div className="flex items-center justify-between p-2 bg-cyan-500/10 rounded border border-cyan-500/20">
                            <span className="text-cyan-400 text-xs font-medium">{language === 'tr' ? 'Toplam Süre' : 'Total Time'}</span>
                            <span className="text-cyan-300 text-sm font-bold">{presentation.timeline.total_time_to_market}</span>
                          </div>
                        )}
                      </div>
                    </PresentationCard>
                  )}

                  {/* Financial Analysis */}
                  {presentation.financial_analysis && (
                    <PresentationCard title={language === 'tr' ? 'Finansal Analiz' : 'Financial Analysis'} icon={DollarSign} color="green">
                      <div className="mb-3 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[10px] font-mono text-amber-200" data-testid="financial-preliminary-note">
                        {language === 'tr'
                          ? 'ÖN BÜTÇE · Tedarikçi teklifleri, uzlaştırılmış BOM ve pazar doğrulaması gerekir.'
                          : 'PRELIMINARY BUDGET · Supplier quotes, a reconciled BOM, and market validation are required.'}
                      </div>
                      <div className="space-y-2">
                        {presentation.financial_analysis.development_cost?.total && (
                          <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                            <span className="text-slate-300 text-xs">{language === 'tr' ? 'Geliştirme Maliyeti' : 'Development Cost'}</span>
                            <span className="text-green-400 text-xs font-medium">{presentation.financial_analysis.development_cost.total}</span>
                          </div>
                        )}
                        {presentation.financial_analysis.unit_production_cost && (
                          <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                            <span className="text-slate-300 text-xs">{language === 'tr' ? 'Birim Maliyet' : 'Unit Cost'}</span>
                            <span className="text-green-400 text-xs font-medium">{presentation.financial_analysis.unit_production_cost}</span>
                          </div>
                        )}
                        {presentation.financial_analysis.suggested_retail_price && (
                          <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded">
                            <span className="text-slate-300 text-xs">{language === 'tr' ? 'Önerilen Fiyat' : 'Retail Price'}</span>
                            <span className="text-green-400 text-xs font-medium">{presentation.financial_analysis.suggested_retail_price}</span>
                          </div>
                        )}
                        {presentation.financial_analysis.profit_margin && (
                          <div className="flex items-center justify-between p-2 bg-green-500/10 rounded border border-green-500/20">
                            <span className="text-green-400 text-xs font-medium">{language === 'tr' ? 'Kar Marjı' : 'Profit Margin'}</span>
                            <span className="text-green-300 text-sm font-bold">{presentation.financial_analysis.profit_margin}</span>
                          </div>
                        )}
                      </div>
                    </PresentationCard>
                  )}

                  {/* Competitive Advantages */}
                  {presentation.competitive_advantages && Array.isArray(presentation.competitive_advantages) && (
                    <PresentationCard title={language === 'tr' ? 'Rekabet Avantajları' : 'Competitive Advantages'} icon={TrendingUp} color="purple">
                      <div className="space-y-2">
                        {presentation.competitive_advantages.slice(0, 3).map((adv, i) => (
                          <div key={i} className="p-2 bg-slate-800/50 rounded">
                            <span className="text-white font-medium text-sm">{typeof adv === 'object' ? adv.advantage : adv}</span>
                            {adv.description && <p className="text-slate-400 text-xs mt-1">{adv.description}</p>}
                          </div>
                        ))}
                      </div>
                    </PresentationCard>
                  )}

                  {/* Risks */}
                  {presentation.risks_and_mitigation && Array.isArray(presentation.risks_and_mitigation) && (
                    <PresentationCard title={language === 'tr' ? 'Riskler & Çözümler' : 'Risks & Mitigation'} icon={AlertTriangle} color="red">
                      <div className="space-y-2">
                        {presentation.risks_and_mitigation.slice(0, 3).map((risk, i) => (
                          <div key={i} className="p-2 bg-slate-800/50 rounded">
                            <div className="flex items-center justify-between">
                              <span className="text-white font-medium text-sm">{typeof risk === 'object' ? risk.risk : risk}</span>
                              {risk.probability && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  risk.probability === 'High' ? 'bg-red-500/20 text-red-400' :
                                  risk.probability === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-green-500/20 text-green-400'
                                }`}>
                                  {risk.probability}
                                </span>
                              )}
                            </div>
                            {risk.mitigation && <p className="text-slate-400 text-xs mt-1">{risk.mitigation}</p>}
                          </div>
                        ))}
                      </div>
                    </PresentationCard>
                  )}

                  {/* Next Steps */}
                  {presentation.next_steps && Array.isArray(presentation.next_steps) && (
                    <PresentationCard title={language === 'tr' ? 'Sonraki Adımlar' : 'Next Steps'} icon={ArrowRight} color="blue">
                      <div className="space-y-2">
                        {presentation.next_steps.slice(0, 4).map((step, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-slate-800/50 rounded">
                            <span className="w-5 h-5 flex items-center justify-center bg-blue-500/20 rounded-full text-blue-400 text-xs font-bold">{i + 1}</span>
                            <span className="text-slate-300 text-xs flex-1">{typeof step === 'object' ? step.step : step}</span>
                            {step.priority && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                step.priority === 'High' ? 'bg-red-500/20 text-red-400' :
                                step.priority === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-green-500/20 text-green-400'
                              }`}>
                                {step.priority}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </PresentationCard>
                  )}

                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Social Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <SocialShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            design={design}
            conceptImage={conceptImage}
            language={language}
          />
        )}
      </AnimatePresence>

      {/* Templates Modal */}
      <AnimatePresence>
        {showTemplatesModal && (
          <TemplatesModal
            isOpen={showTemplatesModal}
            onClose={() => setShowTemplatesModal(false)}
            onSelectTemplate={handleTemplateSelect}
            language={language}
          />
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {showUpgradeModal && (
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            language={language}
            isDemo={isDemo}
            onLogin={handleLogin}
          />
        )}
      </AnimatePresence>

      {/* Intent Review Modal — prompt-fidelity guard (Phase 1 / Intent Lock) */}
      <IntentReviewModal
        open={intentModalOpen}
        intent={parsedIntent}
        loading={intentLoading}
        fallback={intentFallback}
        error={intentError}
        language={language}
        onCancel={() => { setIntentModalOpen(false); setParsedIntent(null); }}
        onConfirm={(confirmedIntent) => {
          setIntentModalOpen(false);
          // Use the confirmed intent to drive the locked generation
          generateProProject(confirmedIntent);
        }}
      />

      {/* Competitive Edge — research competitors, find innovation gaps, design a better one */}
      <CompetitiveEdgeModal
        open={edgeModalOpen}
        prompt={chatMessage}
        language={language}
        projectType={proProjectType}
        onClose={() => setEdgeModalOpen(false)}
        onConfirm={(enrichedIntent, edgeData) => {
          setEdgeModalOpen(false);
          setLastEdgeData(edgeData);
          // Smart view auto-selection — different products need different professional views.
          // - Vehicles/buildings/cabins → add Interior + Cutaway (inhabited spaces)
          // - Smartphones/electronics → add Rear + Exploded (component layout matters)
          const kd = enrichedIntent?.key_dimensions || {};
          const cat = (enrichedIntent?.product_category || '').toLowerCase();
          const ff  = `${kd.form_factor || ''} ${kd.size_hint || ''} ${cat}`.toLowerCase();
          const wantsInterior = /(vehicle|car|sedan|suv|truck|coupe|hatch|crossover|aircraft|drone-cabin|cabin|bus|boat|yacht|room|house|building|cockpit|airliner|jet|train)/.test(ff);
          const wantsExploded = /(phone|smartphone|tablet|laptop|console|headphone|earbud|watch|camera|drone|controller|gadget|electronic|wearable|device)/.test(ff);
          setBriefViews((prev) => {
            const next = new Set(prev);
            if (wantsInterior) { next.add('interior'); next.add('cutaway'); }
            if (wantsExploded) { next.add('rear'); next.add('exploded'); }
            return Array.from(next);
          });
          // Use the enriched intent (which already includes innovation_features) to drive locked generation.
          // The Intent Lock fidelity preface in backend will hold the LLM to it.
          generateProProject(enrichedIntent);
        }}
      />

      {/* 3D Model Viewer Modal */}
      <AnimatePresence
        onExitComplete={() => { setModel3DLoaded(false); setModel3DError(''); }}
      >
        {show3DViewer && model3DUrl && model3DUrl.length > 0 && (
          <ThreeDViewerModal
            model3DUrl={model3DUrl}
            language={language}
            designName={design?.design_specs?.name}
            loaded={model3DLoaded}
            error={model3DError}
            onLoaded={() => setModel3DLoaded(true)}
            onError={(e) => setModel3DError(String(e || 'load-failed'))}
            onClose={() => setShow3DViewer(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}


// ────────────────────────────────────────────────────────────────────
// ThreeDViewerModal — extracted for iter 72 fix:
//   * Reliable close via X button + Escape + backdrop click
//   * Loading spinner ONLY while model-viewer is loading (bound to load/error events)
//   * "3D Ready" badge only shown once loaded (no more contradictory state)
//   * Retry button on error
// ────────────────────────────────────────────────────────────────────
function ThreeDViewerModal({ model3DUrl, language, designName, loaded, error, onLoaded, onError, onClose }) {
  const modelViewerRef = React.useRef(null);
  const [retryKey, setRetryKey] = React.useState(0);

  // Escape-to-close so the modal is never "trapped".
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Bind model-viewer native load / error events (they are DOM events, not React props).
  React.useEffect(() => {
    const el = modelViewerRef.current;
    if (!el) return undefined;
    const handleLoad = () => onLoaded();
    const handleError = (e) => onError(e?.detail?.type || 'load-error');
    el.addEventListener('load', handleLoad);
    el.addEventListener('error', handleError);
    return () => {
      el.removeEventListener('load', handleLoad);
      el.removeEventListener('error', handleError);
    };
  }, [retryKey, model3DUrl, onLoaded, onError]);

  const isHttpUrl = model3DUrl && model3DUrl.startsWith('http');

  return (
    <motion.div
      data-testid="model3d-viewer-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-900 border border-cyan-500/30 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-[#0A0A0A]">
          <div className="flex items-center gap-3">
            <Box className="w-6 h-6 text-cyan-400" />
            <div>
              <h3 className="text-lg font-bold text-white">
                {language === 'tr' ? '3D Prototip Görüntüleyici' : '3D Prototype Viewer'}
              </h3>
              <p className="text-sm text-slate-400">
                {designName || (language === 'tr' ? 'AI Tarafından Oluşturuldu' : 'AI Generated')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={model3DUrl}
              download
              onClick={(e) => e.stopPropagation()}
              className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded-lg flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              {language === 'tr' ? 'GLB İndir' : 'Download GLB'}
            </a>
            {/* Plain <button> (not shadcn) so the click always reaches our handler. */}
            <button
              type="button"
              data-testid="model3d-close-btn"
              aria-label={language === 'tr' ? 'Kapat' : 'Close'}
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="relative bg-[#060710]" style={{ height: '500px' }}>
          {isHttpUrl ? (
            <>
              <model-viewer
                key={retryKey}
                ref={modelViewerRef}
                src={model3DUrl}
                alt={designName || '3D Model'}
                auto-rotate
                camera-controls
                shadow-intensity="1"
                environment-image="neutral"
                exposure="1"
                loading="eager"
                reveal="auto"
                style={{ width: '100%', height: '100%', backgroundColor: '#0f172a' }}
              />
              {/* Loading overlay — shown ONLY until load event fires */}
              {!loaded && !error && (
                <div
                  data-testid="model3d-loading"
                  className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#060710]/85 backdrop-blur-sm"
                >
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-4 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
                    <p className="text-cyan-400">{language === 'tr' ? '3D Model Yükleniyor...' : 'Loading 3D Model...'}</p>
                  </div>
                </div>
              )}
              {/* Error overlay with Retry */}
              {error && (
                <div
                  data-testid="model3d-error"
                  className="absolute inset-0 flex items-center justify-center bg-[#060710]/95"
                >
                  <div className="text-center max-w-sm px-4">
                    <p className="text-rose-300 font-semibold mb-2">
                      {language === 'tr' ? 'Model yüklenemedi' : 'Failed to load model'}
                    </p>
                    <p className="text-slate-400 text-sm mb-4">{String(error).slice(0, 120)}</p>
                    <button
                      type="button"
                      data-testid="model3d-retry-btn"
                      onClick={() => { onError(''); setRetryKey((k) => k + 1); }}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm rounded-lg"
                    >
                      {language === 'tr' ? 'Yeniden Dene' : 'Retry'}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Box className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400">{language === 'tr' ? 'Model URL geçersiz' : 'Invalid model URL'}</p>
                <p className="text-slate-500 text-sm mt-2">{model3DUrl?.substring(0, 50)}...</p>
              </div>
            </div>
          )}
          {loaded && !error && (
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-center pointer-events-none">
              <div className="px-4 py-2 bg-black/50 backdrop-blur rounded-lg text-sm text-slate-300">
                {language === 'tr' ? '🖱️ Döndürmek için sürükleyin • Yakınlaştırmak için kaydırın' : '🖱️ Drag to rotate • Scroll to zoom'}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-slate-800 bg-slate-900/50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-400">
              {language === 'tr' ? 'Meshy AI ile oluşturuldu' : 'Generated with Meshy AI'}
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                {language === 'tr' ? 'GLB Format' : 'GLB Format'}
              </span>
              {/* 3D Ready badge only when model has actually loaded (fixes contradictory
                  simultaneous "Loading" + "Ready" display reported by user). */}
              {loaded && !error && (
                <span
                  data-testid="model3d-ready-badge"
                  className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full"
                >
                  {language === 'tr' ? '3D Hazır' : '3D Ready'}
                </span>
              )}
              {!loaded && !error && (
                <span className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-full">
                  {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
                </span>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
